import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Skill, CastSkillRequest } from '../types/battle';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import BattleUnitEquipment from './BattleUnitEquipment';
import BattleEffects, { FloatingText, PassiveAbilityNotification } from './BattleEffects';
import BattleVictory from './BattleVictory';


interface BattleGridProps {
  battleId: number;
  onExitBattle: () => void;
}

// Helper functions for color-coding resource bars
const getHealthColor = (percentage: number): string => {
  if (percentage > 0.6) return '#4CAF50'; // Green - healthy
  if (percentage > 0.3) return '#FF9800'; // Orange - wounded  
  return '#f44336'; // Red - critical
};

const BattleGrid: React.FC<BattleGridProps> = ({ battleId, onExitBattle }) => {
  const { handleAuthError } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const [battle, setBattle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [movementMode, setMovementMode] = useState(false);
  const [attackMode, setAttackMode] = useState(false);
  const [validMoves, setValidMoves] = useState<{x: number, y: number}[]>([]);
  const [validTargets, setValidTargets] = useState<any[]>([]);
  const [weaponRange, setWeaponRange] = useState<{x: number, y: number}[]>([]);
  const [battleLog, setBattleLog] = useState<string[]>([]);
  const [battleLogInitialized, setBattleLogInitialized] = useState(false);
  const [currentBattleName, setCurrentBattleName] = useState<string | null>(null);
  const [skillMode, setSkillMode] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | any>(null);
  const [isProcessingTurn, setIsProcessingTurn] = useState(false);
  // Removed availableSkills - now only using equipped skill tree nodes
  const [equippedSkills, setEquippedSkills] = useState<any[]>([]);
  const [skillTargets, setSkillTargets] = useState<{x: number, y: number}[]>([]);
  const [skillRange, setSkillRange] = useState<{x: number, y: number}[]>([]);
  const [aoeArea, setAoeArea] = useState<{x: number, y: number}[]>([]);
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [passiveNotifications, setPassiveNotifications] = useState<PassiveAbilityNotification[]>([]);
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [victoryData, setVictoryData] = useState<{experience: number, loot: any[], tilesUnlocked: string[], bonuses?: any, characterXP?: any[]}>({experience: 0, loot: [], tilesUnlocked: []});
  const [turnTransitionBanner, setTurnTransitionBanner] = useState<{visible: boolean, unitName: string, isPlayer: boolean} | null>(null);
  const [battleLogFilter, setBattleLogFilter] = useState<'all' | 'combat' | 'movement' | 'important'>('all');
  const logContentRef = useRef<HTMLDivElement>(null);
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(60);
  const turnTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Grid controls state
  const [gridViewportSize, setGridViewportSize] = useState(() => {
    const saved = localStorage.getItem('battleGridViewportSize');
    return saved ? JSON.parse(saved) : { width: 800, height: 600 };
  });
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('battleGridZoom');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Sync selectedUnit with latest battle state to include status effects and cooldowns
  useEffect(() => {
    if (battle && selectedUnit) {
      const updatedUnit = battle.units?.find((u: any) => u.id === selectedUnit.id);
      if (updatedUnit) {
        setSelectedUnit(updatedUnit);
      }
    }
  }, [battle]); // Re-sync whenever battle state changes

  // Clear action errors when turn changes
  useEffect(() => {
    if (battle?.currentUnitIndex !== undefined) {
      setActionError(null);
    }
  }, [battle?.currentUnitIndex]); // Clear errors whenever the turn changes

  const addToBattleLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setBattleLog(prev => {
      const lastMessage = prev[prev.length - 1];
      const newLogEntry = `[${timestamp}] ${message}`;
      
      // Enhanced deduplication - check for battle start messages specifically
      if (message.includes('- The battle begins!')) {
        const exactBattleStartExists = prev.some(entry => entry.includes(message));
        if (exactBattleStartExists) {
          return prev; // Skip duplicate battle start message
        }
      }
      
      // Filter out any remaining debug messages that might slip through
      if (message.includes('⚠️ DEBUG:') || message.includes('End turn response received') ||
          message.includes('Turn advanced to:') || (message.includes('Processing') && message.includes('AI actions')) ||
          message.includes('AI Action Structure:') || message.includes('Unknown AI action type:')) {
        return prev; // Skip debug messages
      }

      // Prevent duplicate consecutive messages (reduces spam)
      if (lastMessage && lastMessage.includes(message)) {
        return prev; // Skip duplicate
      }
      
      return [...prev, newLogEntry];
    });
  };

  // Enhanced battle log with better user experience
  const addEnhancedBattleLog = (type: string, data: any) => {
    const timestamp = new Date().toLocaleTimeString();
    let message = '';

    // Filter out debug messages completely
    if (type === 'debug' || (typeof data === 'string' && data.includes('DEBUG:'))) {
      return;
    }

    switch (type) {
      case 'battle_start':
        message = `🏛️ **${data.battleName}** - The battle begins!`;
        break;
        
      case 'unit_move':
        // Simple movement without coordinates
        if (data.distance > 3) {
          message = `🏃 ${data.unitName} charges across the battlefield`;
        } else if (data.distance > 1) {
          message = `👣 ${data.unitName} advances forward`;
        } else {
          message = `🔄 ${data.unitName} repositions carefully`;
        }
        break;
        
      case 'attack':
        const healthPercent = data.targetRemainingHP / data.targetMaxHP;
        let healthStatus = '';
        
        if (data.targetDefeated) {
          healthStatus = ' - **💀 DEFEATED!**';
        } else if (healthPercent < 0.25) {
          healthStatus = ' - *critically wounded*';
        } else if (healthPercent < 0.5) {
          healthStatus = ' - *badly wounded*';
        } else if (healthPercent < 0.75) {
          healthStatus = ' - *wounded*';
        }

        // Determine attack type based on damage
        const avgDamage = data.targetMaxHP * 0.15; // Estimate average damage
        let attackDesc = '⚔️';
        
        if (data.damage > avgDamage * 1.5) {
          attackDesc = '💥 **CRITICAL HIT!**';
        } else if (data.damage > avgDamage * 1.2) {
          attackDesc = '🗡️ *Powerful strike!*';
        }

        message = `${attackDesc} ${data.attackerName} strikes ${data.targetName} for ${data.damage} damage${healthStatus}`;
        break;
        
      case 'skill_cast':
        const skillEmoji = getSkillEmoji(data.skillName);
        let skillDesc = getSkillDescription(data.skillName, data.affectedCount);

        if (data.damage && data.affectedCount > 1) {
          message = `${skillEmoji} ${data.casterName} ${skillDesc} hitting ${data.affectedCount} enemies for ${data.damage} damage!`;
        } else if (data.damage) {
          message = `${skillEmoji} ${data.casterName} ${skillDesc} for ${data.damage} damage!`;
        } else if (data.healing) {
          message = `💚 ${data.casterName} ${skillDesc} restoring ${data.healing} health!`;
        } else {
          message = `${skillEmoji} ${data.casterName} ${skillDesc}`;
        }
        break;
        
      case 'turn_start':
        // Only show for player units to reduce clutter - AI actions will be shown when they act
        if (data.isPlayerUnit) {
          message = `🎯 **${data.unitName}'s turn**`;
        } else {
          // Skip AI turn announcements but their actions will still be logged
          return;
        }
        break;
        
      case 'victory':
        message = `🏆 **VICTORY!** All enemies have been vanquished!`;
        break;
        
      case 'defeat':
        message = `💀 **DEFEAT!** Your party has fallen in battle.`;
        break;
        
      case 'materials_found':
        if (data.materials && data.materials.length > 0) {
          message = `💎 **Loot discovered:** ${data.materials.map((m: any) => `${m.quantity}x ${m.materialName}`).join(', ')}`;
        }
        break;
        
      case 'recipes_discovered':
        if (data.recipes && data.recipes.length > 0) {
          message = `📜 **New recipes learned:** ${data.recipes.map((r: any) => r.recipeName).join(', ')}`;
        }
        break;
        
      case 'xp_gain':
        message = `📈 ${data.unitName} gains ${data.xpAmount} XP!`;
        break;
        
      case 'error':
        message = data.message;
        break;
        
      case 'passive_ability':
        message = `🔮 ${data.unitName}'s ${data.abilityName}: ${data.description}`;
        break;
        
      case 'debug':
        // Show debug messages to help troubleshoot AI actions
        message = `⚠️ DEBUG: ${data.message}`;
        break;
        
      default:
        message = data.message || '';
    }

    if (message) {
      // Prevent duplicate consecutive messages (reduces spam)
      setBattleLog(prev => {
        const lastMessage = prev[prev.length - 1];
        const newLogEntry = `[${timestamp}] ${message}`;
        
        // Enhanced deduplication - check for similar battle start messages specifically
        if (message.includes('- The battle begins!')) {
          const battleStartExists = prev.some(entry => entry.includes('- The battle begins!'));
          if (battleStartExists) {
            return prev; // Skip duplicate battle start message
          }
        }
        
        // General deduplication - check if the core message content is the same (ignoring timestamp)
        if (lastMessage && lastMessage.includes(message)) {
          return prev; // Skip duplicate
        }
        
        return [...prev, newLogEntry];
      });
    }
  };

  const getSkillEmoji = (skillName: string): string => {
    const skillEmojis: { [key: string]: string } = {
      'Fireball': '🔥',
      'Magic Missile': '✨',
      'Heal': '💚',
      'Mass Heal': '💞',
      'Lightning Bolt': '⚡',
      'Ice Shard': '🧊',
      'Teleport': '🌟',
      'Shield': '🛡️'
    };
    return skillEmojis[skillName] || '✨';
  };

  const getSkillDescription = (skillName: string, affectedCount: number = 1): string => {
    const descriptions: { [key: string]: string } = {
      'Fireball': 'hurls a blazing fireball',
      'Magic Missile': 'launches magical missiles',
      'Heal': 'channels healing energy',
      'Mass Heal': 'radiates healing light',
      'Lightning Bolt': 'calls down lightning',
      'Ice Shard': 'conjures razor-sharp ice',
      'Teleport': 'vanishes and reappears elsewhere',
      'Shield': 'conjures a protective barrier'
    };
    return descriptions[skillName] || `casts ${skillName}`;
  };

  const addFloatingText = (text: string, type: FloatingText['type'], x: number, y: number) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newText: FloatingText = { id, text, type, x, y };
    setFloatingTexts(prev => [...prev, newText]);
  };

  const addPassiveNotification = (
    abilityName: string, 
    unitName: string, 
    description: string, 
    type: PassiveAbilityNotification['type']
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const notification: PassiveAbilityNotification = { id, abilityName, unitName, description, type };
    setPassiveNotifications(prev => [...prev, notification]);
  };

  const removeFloatingText = (id: string) => {
    setFloatingTexts(prev => prev.filter(text => text.id !== id));
  };

  const removePassiveNotification = (id: string) => {
    setPassiveNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const showTurnTransition = (unitName: string, isPlayer: boolean) => {
    setTurnTransitionBanner({ visible: true, unitName, isPlayer });

    // Auto-hide after 2 seconds
    setTimeout(() => {
      setTurnTransitionBanner(null);
    }, 2000);
  };

  // Filter battle log based on selected filter
  const getFilteredBattleLog = (): string[] => {
    if (battleLogFilter === 'all') {
      return battleLog;
    }

    return battleLog.filter(entry => {
      const message = entry.toLowerCase();

      switch (battleLogFilter) {
        case 'combat':
          // Show attacks, skills, damage, defeats
          return message.includes('⚔️') || message.includes('💥') || message.includes('🗡️') ||
                 message.includes('🔥') || message.includes('✨') || message.includes('⚡') ||
                 message.includes('🧊') || message.includes('💚') || message.includes('💞') ||
                 message.includes('strikes') || message.includes('damage') || message.includes('defeated') ||
                 message.includes('casts') || message.includes('hurls') || message.includes('launches');

        case 'movement':
          // Show movement and positioning
          return message.includes('🏃') || message.includes('👣') || message.includes('🔄') ||
                 message.includes('charges') || message.includes('advances') || message.includes('repositions');

        case 'important':
          // Show critical events: defeats, victories, turn starts, loot, recipes
          return message.includes('💀 defeated') || message.includes('🏆') || message.includes('💎') ||
                 message.includes('📜') || message.includes('📈') || message.includes('🎯') ||
                 message.includes('victory') || message.includes('defeat') || message.includes('level up') ||
                 message.includes('loot') || message.includes('recipe') || message.includes('turn');

        default:
          return true;
      }
    });
  };

  const processPassiveEvents = (passiveEvents: any[]) => {
    if (!passiveEvents || passiveEvents.length === 0) return;
    
    passiveEvents.forEach((event: any) => {
      // Add passive ability notification
      addPassiveNotification(
        event.abilityName,
        event.unitName,
        event.description,
        event.type
      );
      
      // Add battle log entry
      addEnhancedBattleLog('passive_ability', {
        unitName: event.unitName,
        abilityName: event.abilityName,
        description: event.description
      });
      
      // Add floating text if there's numeric data to show
      if (event.eventData) {
        let floatingText = '';
        let textType: FloatingText['type'] = 'passive';
        
        if (event.eventData.healing) {
          floatingText = `+${event.eventData.healing} HP`;
          textType = 'healing';
        } else if (event.eventData.damage) {
          floatingText = `${event.eventData.damage} DMG`;
          textType = 'damage';
        } else if (event.eventData.apGained) {
          floatingText = `+${event.eventData.apGained} AP`;
          textType = 'passive';
          textType = 'passive';
        }
        
        if (floatingText && battle?.units) {
          // Find the unit position for floating text
          const unit = battle.units.find((u: any) => 
            u.character?.name === event.unitName || u.enemyName === event.unitName
          );
          if (unit) {
            const x = unit.x * 50 + 25; // Grid cell size + offset
            const y = unit.y * 50 + 25;
            addFloatingText(floatingText, textType, x, y);
          }
        }
      }
    });
  };

  // Auto-scroll to bottom when new log entries are added
  useEffect(() => {
    if (logContentRef.current) {
      logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
    }
  }, [battleLog]);

  // Battle state persistence - save to localStorage
  useEffect(() => {
    if (battle && battleId) {
      const persistenceKey = `battle_${battleId}_state`;
      const battleState = {
        battle,
        battleLog,
        selectedUnit: selectedUnit ? { id: selectedUnit.id } : null,
        timestamp: Date.now()
      };

      try {
        localStorage.setItem(persistenceKey, JSON.stringify(battleState));
      } catch (error) {
        console.warn('Failed to persist battle state:', error);
      }
    }
  }, [battle, battleLog, selectedUnit, battleId]);

  // Restore battle state from localStorage on mount
  useEffect(() => {
    const persistenceKey = `battle_${battleId}_state`;

    try {
      const savedStateJson = localStorage.getItem(persistenceKey);
      if (savedStateJson) {
        const savedState = JSON.parse(savedStateJson);

        // Check if saved state is recent (within last 30 minutes)
        const timeDiff = Date.now() - (savedState.timestamp || 0);
        const thirtyMinutes = 30 * 60 * 1000;

        if (timeDiff < thirtyMinutes && savedState.battle) {
          // Restore battle log if it exists and is not empty
          if (savedState.battleLog && savedState.battleLog.length > 0) {
            setBattleLog(savedState.battleLog);
            setBattleLogInitialized(true);
            if (savedState.battle.battleName) {
              setCurrentBattleName(savedState.battle.battleName);
            }
          }

          // Note: We don't restore the full battle state here
          // fetchBattleState will get the authoritative state from the server
          // But we've restored the battle log for continuity
        } else {
          // Clear old saved state
          localStorage.removeItem(persistenceKey);
        }
      }
    } catch (error) {
      console.warn('Failed to restore battle state:', error);
      // Clear corrupted data
      localStorage.removeItem(persistenceKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleId]); // Only run once on mount

  // Clear battle state from localStorage when battle ends and show victory/defeat screen
  useEffect(() => {
    if (battle && (battle.status === 'Victory' || battle.status === 'Defeat')) {
      const persistenceKey = `battle_${battleId}_state`;
      try {
        localStorage.removeItem(persistenceKey);
      } catch (error) {
        console.warn('Failed to clear battle state:', error);
      }

      // Show victory screen if not already showing
      if (battle.status === 'Victory' && !showVictoryScreen) {
        // Extract victory data from battle state
        const loot = battle.materialsAwarded || [];
        const tilesUnlocked = battle.tilesUnlocked || [];
        const characterXP = battle.characterXP || [];
        setVictoryData({ experience: 0, loot, tilesUnlocked, bonuses: null, characterXP });
        setShowVictoryScreen(true);
      }
    }
  }, [battle, battleId, showVictoryScreen]);

  const fetchBattleState = useCallback(async (): Promise<void> => {
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}`);

      if (response.ok) {
        const data = await response.json();
        const battleData = data.battleState || data;
        setBattle(battleData);

        // Show equipment debug info if available
        if (battleData.equipmentDebug && battleData.equipmentDebug.length > 0) {
          console.log('🔧 EQUIPMENT LOADING DEBUG:');
          battleData.equipmentDebug.forEach((msg: string) => console.log(msg));
        }

        // Expose battle state to window for debugging
        (window as any).battleState = battleData;


        // Initialize battle log only once per battle
        if (battleData.battleName && (!battleLogInitialized || currentBattleName !== battleData.battleName)) {
          // Check if battle start message already exists in log
          const battleStartExists = battleLog.some(entry => entry.includes('- The battle begins!') && entry.includes(battleData.battleName));

          if (!battleStartExists) {
            addEnhancedBattleLog('battle_start', { battleName: battleData.battleName });
            setBattleLogInitialized(true);
            setCurrentBattleName(battleData.battleName);
          }
        }

        // Auto-select current turn unit if it's a player unit
        if (battleData.turnOrder && battleData.turnOrder.length > 0) {
          const currentIndex = battleData.currentUnitIndex || 0;
          const currentTurnUnit = battleData.turnOrder[currentIndex] || null;

          if (currentTurnUnit && currentTurnUnit.isPlayerUnit && battleData.status === 'InProgress') {
            // Auto-select the current player unit
            const playerUnit = battleData.units?.find((unit: any) =>
              unit.isPlayerUnit && unit.id === currentTurnUnit.unitId
            );
            if (playerUnit && (!selectedUnit || selectedUnit.id !== playerUnit.id)) {
              setSelectedUnit(playerUnit);
              // Clear all skill state when switching to a different unit
              setSkillMode(false);
              setSelectedSkill(null);
              setSkillRange([]);
              setSkillTargets([]);
              setAoeArea([]);
            }
          }

          if (currentTurnUnit && !currentTurnUnit.isPlayerUnit && battleData.status === 'InProgress' && !isProcessingTurn) {
            // Process immediately to prevent state timing issues
            endTurn(currentTurnUnit).catch(error => {
              console.error('Error calling endTurn for AI unit:', error);
            });
          }
        }
      } else {
        setError('Failed to load battle');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return; // Don't set error state, user will be redirected to login
      }
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleId, handleAuthError]);

  useEffect(() => {
    fetchBattleState();
  }, [battleId, fetchBattleState]);

  // Auto-trigger battle tutorial for first-time players
  useEffect(() => {
    if (battle && !loading) {
      checkAndStartTutorial('battle-basics');
    }
  }, [battle, loading, checkAndStartTutorial]);

  // Fetch available skills when a player unit is selected
  useEffect(() => {
    if (selectedUnit && selectedUnit.isPlayerUnit) {
      // Fetch equipped skills if unit has a characterId
      if (selectedUnit.characterId) {
        fetchEquippedSkills(selectedUnit.characterId).catch(error => {
          console.error('Error fetching equipped skills:', error);
        });
      }
    } else {
      setEquippedSkills([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  // Turn timer countdown
  useEffect(() => {
    // Reset timer when turn changes or battle loads
    setTurnTimeRemaining(60);

    // Clear any existing timer
    if (turnTimerRef.current) {
      clearInterval(turnTimerRef.current);
      turnTimerRef.current = null;
    }

    // Only run timer if battle is in progress
    if (battle?.status !== 'InProgress') {
      return;
    }

    // Start countdown timer
    turnTimerRef.current = setInterval(() => {
      setTurnTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - auto end turn for current unit
          if (battle && currentTurnUnit) {
            addToBattleLog(`⏰ Time's up for ${currentTurnUnit.name}! Ending turn automatically.`);
            endTurn(currentTurnUnit).catch(error => {
              console.error('Error auto-ending turn:', error);
            });
          }
          return 60; // Reset for next turn
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
    };
  }, [battle?.currentUnitIndex, battle?.status]); // Reset when turn changes

  // Save grid preferences to localStorage
  useEffect(() => {
    localStorage.setItem('battleGridViewportSize', JSON.stringify(gridViewportSize));
  }, [gridViewportSize]);

  useEffect(() => {
    localStorage.setItem('battleGridZoom', zoomLevel.toString());
  }, [zoomLevel]);

  // Keyboard controls for grid panning and action hotkeys
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Don't handle if typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Arrow keys for panning
      const panSpeed = 50;
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setPanOffset(prev => ({ x: prev.x, y: prev.y + panSpeed }));
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setPanOffset(prev => ({ x: prev.x, y: prev.y - panSpeed }));
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPanOffset(prev => ({ x: prev.x + panSpeed, y: prev.y }));
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPanOffset(prev => ({ x: prev.x - panSpeed, y: prev.y }));
        return;
      }

      // Action hotkeys - only if a player unit is selected
      if (!selectedUnit || !selectedUnit.isPlayerUnit) {
        return;
      }

      const key = event.key;

      // Backtick (`) for Attack
      if (key === '`' || key === '~') {
        event.preventDefault();
        if (selectedUnit.equipment?.weapon && !attackMode) {
          enterAttackMode();
        }
        return;
      }

      // 1-9, 0 for skills
      let skillSlotIndex = -1;

      if (key >= '1' && key <= '9') {
        skillSlotIndex = parseInt(key) - 1; // 1 maps to hotbarSlot 0, 2 to 1, etc.
      } else if (key === '0') {
        skillSlotIndex = 9; // 0 maps to hotbarSlot 9
      }

      if (skillSlotIndex === -1) return;

      event.preventDefault();

      // Find skill at this hotbar slot
      const equippedSkill = equippedSkills.find(es => es.hotbarSlot === skillSlotIndex);
      if (equippedSkill) {
        const skill = equippedSkill.skill;
        const cooldownInfo = selectedUnit.skillCooldowns?.find((cd: any) => cd.skillNodeId === skill.id);
        const isOnCooldown = cooldownInfo && cooldownInfo.turnsRemaining > 0;
        const stunned = isUnitStunned(selectedUnit);
        const canAfford = selectedUnit.stats.currentAP >= (skill.apCost || 0);

        if (!isOnCooldown && !stunned && canAfford) {
          selectSkillNode(equippedSkill);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedUnit, equippedSkills, attackMode, skillMode, panOffset]);

  // Zoom control functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2.0)); // Max 2x zoom
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5)); // Min 0.5x zoom
  };

  const handleResetZoom = () => {
    setZoomLevel(1.0);
    setPanOffset({ x: 0, y: 0 });
  };

  // Pan/drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start dragging if clicking on the grid container background, not on tiles or units
    if (e.target === gridContainerRef.current || (e.target as HTMLElement).classList.contains('battle-grid')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent, edge: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeEdge(edge);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: gridViewportSize.width,
      height: gridViewportSize.height
    });
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleResizeMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      if (resizeEdge?.includes('right')) {
        newWidth = Math.max(400, resizeStart.width + deltaX);
      }
      if (resizeEdge?.includes('left')) {
        newWidth = Math.max(400, resizeStart.width - deltaX);
      }
      if (resizeEdge?.includes('bottom')) {
        newHeight = Math.max(300, resizeStart.height + deltaY);
      }
      if (resizeEdge?.includes('top')) {
        newHeight = Math.max(300, resizeStart.height - deltaY);
      }

      setGridViewportSize({ width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      setResizeEdge(null);
    };

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);

    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeEdge, resizeStart]);

  const getTileColor = (tile: any) => {
    if (!tile) return '#90EE90'; // Default color for undefined tiles

    // Hazard overlay takes priority for visual effect
    if (tile.hazardType) {
      switch (tile.hazardType) {
        case 'Fire': return '#FF4500'; // Orange-red fire
        case 'Poison': return '#9ACD32'; // Yellow-green poison
        case 'Ice': return '#00CED1'; // Dark turquoise ice
        case 'Lightning': return '#FFD700'; // Gold lightning
        case 'Acid': return '#ADFF2F'; // Green-yellow acid
        case 'Thorns': return '#8B4513'; // Saddle brown thorns
        case 'Miasma': return '#4B0082'; // Indigo miasma
      }
    }

    // Standard terrain colors
    switch (tile.type) {
      case 'Normal': return '#90EE90'; // Light green
      case 'Grass': return '#7CFC00'; // Lawn green
      case 'Forest': return '#228B22'; // Forest green - provides evasion
      case 'Mountain': return '#8B7355'; // Burlywood4 - provides defense
      case 'Water': return '#87CEEB'; // Sky blue
      case 'DeepWater': return '#4682B4'; // Steel blue (impassable)
      case 'Sand': return '#F4A460'; // Sandy brown
      case 'Snow': return '#F0F8FF'; // Alice blue
      case 'Swamp': return '#556B2F'; // Dark olive green
      case 'Road': return '#A9A9A9'; // Dark gray (fast movement)
      case 'Bridge': return '#8B4513'; // Saddle brown
      case 'Lava': return '#FF6347'; // Tomato red
      case 'Pit': return '#2F4F4F'; // Dark slate gray (impassable)
      case 'HighGround': return '#D2B48C'; // Tan (elevated)
      case 'Rock': return '#A0522D'; // Sienna (blocks vision)
      case 'Ice': return '#B0E0E6'; // Powder blue (slippery)
      case 'Rubble': return '#808080'; // Gray (destroyed terrain)
      case 'Difficult': return '#DEB887'; // Burlywood
      case 'Impassable': return '#696969'; // Dim gray
      default: return '#90EE90';
    }
  };

  const getUnitAtPosition = (x: number, y: number) => {
    return battle?.units?.find((unit: any) => unit.position.x === x && unit.position.y === y);
  };

  const getTileAtPosition = (x: number, y: number) => {
    return battle?.tiles?.find((tile: any) => tile.x === x && tile.y === y);
  };

  const handleTileClick = (x: number, y: number) => {
    // Disable all tile interactions when battle has ended
    if (battle?.status === 'Victory' || battle?.status === 'Defeat') {
      return;
    }
    
    if (movementMode && selectedUnit) {
      // Try to move the selected unit
      const isValidMove = validMoves.some(move => move.x === x && move.y === y);
      if (isValidMove) {
        moveUnit(selectedUnit.id, x, y);
      }
      return;
    }

    if (attackMode && selectedUnit) {
      // Try to attack a unit at this position
      const targetUnit = getUnitAtPosition(x, y);
      if (targetUnit && validTargets.some(target => target.id === targetUnit.id)) {
        attackUnit(selectedUnit.id, targetUnit.id);
      }
      return;
    }

    if (skillMode && selectedSkill && selectedUnit) {
      // Try to execute skill node at this position
      const isValidTarget = skillTargets.some(target => target.x === x && target.y === y);
      if (isValidTarget) {
        executeSkill(x, y);
      }
      return;
    }

    // If we're in skill mode but no skill selected, prevent any clicks
    if (skillMode && !selectedSkill) {
      setActionError('Please select a skill first, or click Cancel Skill to exit skill mode.');
      return;
    }

    const unit = getUnitAtPosition(x, y);
    if (unit) {
      // Check if it's this unit's turn before allowing selection
      const currentTurnUnit = getCurrentTurnUnit();
      
      if (unit.isPlayerUnit && currentTurnUnit && currentTurnUnit.unitId === unit.id) {
        // It's this player unit's turn - allow selection
        setSelectedUnit(unit);
        setMovementMode(false);
        setAttackMode(false);
        setSkillMode(false);
        setValidMoves([]);
        setValidTargets([]);
        setSkillTargets([]);
        setSelectedSkill(null);
        setSkillRange([]);
        setAoeArea([]);
        setActionError(null); // Clear any previous action errors
      } else if (unit.isPlayerUnit && currentTurnUnit && currentTurnUnit.unitId !== unit.id) {
        // It's a different player unit's turn - show message and don't allow selection
        addEnhancedBattleLog('error', {
          message: `❌ It's ${currentTurnUnit.name}'s turn, not ${unit.name}'s turn!`
        });
        setActionError(`It's ${currentTurnUnit.name}'s turn. You can only control the active unit.`);
      } else if (!unit.isPlayerUnit) {
        // Enemy unit selected - allow selection for targeting purposes
        setSelectedUnit(unit);
        setMovementMode(false);
        setAttackMode(false);
        setSkillMode(false);
        setValidMoves([]);
        setValidTargets([]);
        setSkillTargets([]);
        setSelectedSkill(null);
        setSkillRange([]);
        setAoeArea([]);
      }
    } else {
      setSelectedUnit(null);
      setMovementMode(false);
      setAttackMode(false);
      setSkillMode(false);
      setValidMoves([]);
      setValidTargets([]);
      setSkillTargets([]);
      setSelectedSkill(null);
      setSkillRange([]);
      setAoeArea([]);
    }
  };

  const calculateValidMoves = async (unit: any) => {
    if (!unit || !unit.isPlayerUnit || unit.stats.currentMovement <= 0) return [];
    
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/valid-moves/${unit.id}`, {
        method: 'GET'
      });

      if (response.ok) {
        const result = await response.json();
        return result.validMoves.map((move: any) => ({ x: move.x, y: move.y }));
      } else {
        console.error('Failed to fetch valid moves');
        return [];
      }
    } catch (error) {
      console.error('Error calculating valid moves:', error);
      return [];
    }
  };

  const enterMovementMode = async () => {
    if (selectedUnit && selectedUnit.isPlayerUnit && selectedUnit.stats.currentMovement > 0) {
      const currentTurnUnit = getCurrentTurnUnit();
      if (currentTurnUnit && currentTurnUnit.unitId === selectedUnit.id) {
        setMovementMode(true);
        const moves = await calculateValidMoves(selectedUnit);
        setValidMoves(moves);
        setActionError(null); // Clear any previous action errors
      } else {
        addEnhancedBattleLog('error', {
          message: `❌ Cannot move ${selectedUnit.name} - it's ${currentTurnUnit?.name || 'another unit'}'s turn!`
        });
        setActionError(`It's ${currentTurnUnit?.name || 'another unit'}'s turn. You can only move the active unit.`);
      }
    }
  };

  const calculateValidTargets = (unit: any) => {
    if (!unit || !unit.isPlayerUnit || unit.stats.currentAP <= 0) return [];

    const targets: any[] = [];

    // Get weapon range from equipped weapon
    const weapon = unit.equipment?.weapon;
    const minRange = weapon?.minRange || 1;
    const maxRange = weapon?.maxRange || 1;

    battle?.units?.forEach((targetUnit: any) => {
      if (!targetUnit.isPlayerUnit) { // Can only attack enemy units
        const distance = Math.abs(unit.position.x - targetUnit.position.x) +
                        Math.abs(unit.position.y - targetUnit.position.y);
        if (distance >= minRange && distance <= maxRange) {
          targets.push(targetUnit);
        }
      }
    });

    return targets;
  };

  // Calculate all tiles within weapon range for visual indicator
  const calculateWeaponRange = (unit: any): {x: number, y: number}[] => {
    if (!unit || !battle) return [];

    const weapon = unit.equipment?.weapon;
    const minRange = weapon?.minRange || 1;
    const maxRange = weapon?.maxRange || 1;

    console.log(`🎯 Calculating weapon range for ${unit.name}:`, {
      weaponName: weapon?.itemName || 'No weapon',
      minRange,
      maxRange,
      position: unit.position,
      fullWeapon: weapon
    });

    const tiles: {x: number, y: number}[] = [];

    const gridWidth = battle.gridWidth || 12;
    const gridHeight = battle.gridHeight || 8;

    // Calculate all tiles within weapon range (Manhattan distance)
    for (let x = 0; x < gridWidth; x++) {
      for (let y = 0; y < gridHeight; y++) {
        const distance = Math.abs(unit.position.x - x) + Math.abs(unit.position.y - y);
        if (distance >= minRange && distance <= maxRange) {
          tiles.push({x, y});
        }
      }
    }

    console.log(`🎯 Found ${tiles.length} tiles in weapon range`);
    return tiles;
  };

  const calculateAOEPreview = (attackerPos: {x: number, y: number}, targetX: number, targetY: number, aoeShape: string): {x: number, y: number}[] => {
    const tiles: {x: number, y: number}[] = [];
    tiles.push({x: targetX, y: targetY}); // Always include primary target

    if (!aoeShape || aoeShape === 'Single') {
      return tiles;
    }

    // Calculate direction vector
    const dx = targetX - attackerPos.x;
    const dy = targetY - attackerPos.y;
    const dirX = dx !== 0 ? dx / Math.abs(dx) : 0;
    const dirY = dy !== 0 ? dy / Math.abs(dy) : 0;

    switch (aoeShape) {
      case 'Lance': // 3 tiles in a line
        tiles.push({x: targetX + dirX, y: targetY + dirY});
        tiles.push({x: targetX + dirX * 2, y: targetY + dirY * 2});
        break;

      case 'Cleave': // 3 tiles across
        if (dirX !== 0 && dirY === 0) {
          tiles.push({x: targetX, y: targetY - 1});
          tiles.push({x: targetX, y: targetY + 1});
        } else if (dirX === 0 && dirY !== 0) {
          tiles.push({x: targetX - 1, y: targetY});
          tiles.push({x: targetX + 1, y: targetY});
        } else {
          tiles.push({x: targetX + dirY, y: targetY + dirX});
          tiles.push({x: targetX - dirY, y: targetY - dirX});
        }
        break;

      case 'Cone': // 8-tile spread
        for (let x = -1; x <= 1; x++) {
          for (let y = -1; y <= 1; y++) {
            if (x === 0 && y === 0) continue;
            tiles.push({x: targetX + x, y: targetY + y});
          }
        }
        break;

      case 'Circle': // 3x3 area
        for (let x = -1; x <= 1; x++) {
          for (let y = -1; y <= 1; y++) {
            if (x === 0 && y === 0) continue;
            tiles.push({x: targetX + x, y: targetY + y});
          }
        }
        break;
    }

    return tiles;
  };

  const enterAttackMode = () => {
    if (selectedUnit && selectedUnit.isPlayerUnit && selectedUnit.stats.currentAP > 0) {
      const currentTurnUnit = getCurrentTurnUnit();
      if (currentTurnUnit && currentTurnUnit.unitId === selectedUnit.id) {
        console.log('🔍 Selected unit full data:', selectedUnit);
        console.log('🔍 Equipment field:', selectedUnit.equipment);
        console.log('🔍 All unit keys:', Object.keys(selectedUnit));

        // Clear skill mode when entering attack mode
        setSkillMode(false);
        setSkillRange([]);
        setSkillTargets([]);
        setSelectedSkill(null);

        setAttackMode(true);
        setValidTargets(calculateValidTargets(selectedUnit));
        setWeaponRange(calculateWeaponRange(selectedUnit)); // Show weapon range when entering attack mode
        setActionError(null); // Clear any previous action errors
      } else {
        addEnhancedBattleLog('error', {
          message: `❌ Cannot attack with ${selectedUnit.name} - it's ${currentTurnUnit?.name || 'another unit'}'s turn!`
        });
        setActionError(`It's ${currentTurnUnit?.name || 'another unit'}'s turn. You can only attack with the active unit.`);
      }
    }
  };

  const cancelMovement = () => {
    setMovementMode(false);
    setValidMoves([]);
  };

  const cancelAttack = () => {
    setAttackMode(false);
    setValidTargets([]);
    setWeaponRange([]); // Clear weapon range when canceling attack
  };

  // Removed fetchAvailableSkills - now only using equipped skill tree nodes

  const fetchEquippedSkills = async (characterId: number) => {
    try {
      const response = await makeAuthenticatedRequest(`/skillhotbar/character/${characterId}`);

      if (response.ok) {
        const skills = await response.json();
        setEquippedSkills(skills);
      } else {
        // It's okay if there are no equipped skills
        setEquippedSkills([]);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      // Don't show error for no equipped skills
      setEquippedSkills([]);
    }
  };

  // Helper function to check if any skills can be cast
  const canCastAnySkill = (unit: any, skills: Skill[]): boolean => {
    // If no skills loaded yet, assume false (button should be disabled until skills load)
    if (!skills || skills.length === 0) {
      return false;
    }

    // Check if any skill can be afforded with current AP
    return skills.some(skill =>
      unit.stats.currentAP >= skill.apCost
    );
  };

  // Removed enterSkillMode - now using skill tree nodes directly

  const cancelSkill = () => {
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillTargets([]);
    setSkillRange([]);
    setAoeArea([]);
  };

  // Removed selectSkill, calculateSkillRange, calculateAoeArea, calculateSkillTargets,
  // isValidSkillTarget, getTargetTypeString, and castSkill - now using skill tree nodes only

  // Helper functions to check status effects
  const isUnitRooted = (unit: any): boolean => {
    if (!unit || !unit.statusEffects) return false;
    return unit.statusEffects.some((effect: any) =>
      effect.isRooted && effect.turnsRemaining > 0
    );
  };

  const isUnitStunned = (unit: any): boolean => {
    if (!unit || !unit.statusEffects) return false;
    return unit.statusEffects.some((effect: any) =>
      effect.isStunned && effect.turnsRemaining > 0
    );
  };

  const getDisabledReason = (unit: any, action: string): string | null => {
    if (!unit) return null;

    const stunnedEffect = unit.statusEffects?.find((e: any) => e.isStunned && e.turnsRemaining > 0);
    if (stunnedEffect) {
      return `Unit is stunned by ${stunnedEffect.effectName} (${stunnedEffect.turnsRemaining} turns) - cannot ${action}`;
    }

    if (action === 'move') {
      const rootedEffect = unit.statusEffects?.find((e: any) => e.isRooted && e.turnsRemaining > 0);
      if (rootedEffect) {
        return `Unit is rooted by ${rootedEffect.effectName} (${rootedEffect.turnsRemaining} turns) - cannot move`;
      }
    }

    return null;
  };

  // Skill usage functions (similar to skill casting) - uses skill node data
  const selectSkillNode = (skill: any) => {
    // Check if this is a self-targeting skill
    const selfTargetSkills = ['Stealth', 'Camouflage', 'Berserker Rage', 'Blessing', 'Divine Protection'];

    if (selfTargetSkills.includes(skill.skill.name)) {
      // Auto-cast on self immediately
      if (selectedUnit) {
        executeSkillDirect(skill, selectedUnit.position.x, selectedUnit.position.y);
      }
      return;
    }

    // Clear attack mode and movement mode when entering skill mode
    setAttackMode(false);
    setWeaponRange([]);
    setValidTargets([]);
    setMovementMode(false);
    setValidMoves([]);

    setSelectedSkill(skill);
    setSkillMode(true);
    // For now, skills target enemies in range (can be extended later for different skill types)
    const targets = calculateSkillNodeTargets(skill);
    setSkillTargets(targets);
  };

  const cancelSkillNode = () => {
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillTargets([]);
    setSkillRange([]);
  };

  const calculateSkillNodeTargets = (skill: any): {x: number, y: number}[] => {
    if (!selectedUnit || !skill) return [];

    const targets: {x: number, y: number}[] = [];
    const rangePositions: {x: number, y: number}[] = [];
    const skillRange = skill.skill?.maxRange || skill.skill?.range || 5; // Use maxRange first, fallback to range
    const minRange = skill.skill?.minRange || 0; // Get minimum range
    const skillName = skill.skill?.name || '';

    // Determine if skill targets ground, allies, or enemies based on skill name/type
    const groundTargetSkills = ['Leap', 'Teleport', 'Shadow Step'];
    const healingSkills = ['Lesser Heal', 'Greater Heal', 'Area Heal', 'Lay on Hands', 'Blessing', 'Divine Protection'];
    const buffSkills = ['Blessing', 'Divine Protection', 'Haste'];
    const swapSkills = ['Switch Places']; // Can target any unit
    const pushPullSkills = ['Push', 'Pull']; // Target enemies to push/pull them

    const isGroundTarget = groundTargetSkills.includes(skillName);
    const isHealingOrBuff = healingSkills.includes(skillName) || buffSkills.includes(skillName);
    const isSwapSkill = swapSkills.includes(skillName);
    const isPushPull = pushPullSkills.includes(skillName);

    // Calculate all tiles in range and valid targets
    for (let x = 0; x < battle.gridWidth; x++) {
      for (let y = 0; y < battle.gridHeight; y++) {
        const distance = Math.abs(selectedUnit.position.x - x) + Math.abs(selectedUnit.position.y - y);

        // Check if tile is within min and max range
        const allowSelfTarget = isHealingOrBuff && distance === 0;
        const inRange = distance >= minRange && distance <= skillRange && (distance > 0 || allowSelfTarget);

        if (inRange) {
          // Ground-targeted skills can target any empty passable tile
          if (isGroundTarget) {
            const unitAtPosition = getUnitAtPosition(x, y);
            const tile = battle.tiles.find((t: any) => t.x === x && t.y === y);

            // Shadow Step can only target tiles adjacent to enemies
            if (skillName === 'Shadow Step' && !unitAtPosition && tile && tile.isPassable) {
              // Check if this tile is adjacent to any enemy
              const isAdjacentToEnemy = battle.units.some((u: any) => {
                if (u.isPlayerUnit || u.stats.currentHP <= 0) return false;
                const dist = Math.abs(u.position.x - x) + Math.abs(u.position.y - y);
                return dist === 1; // Adjacent means distance of 1
              });
              if (isAdjacentToEnemy) {
                targets.push({ x, y });
                // Only add to range indicator if it's a valid target for Shadow Step
                if (distance > 0) {
                  rangePositions.push({ x, y });
                }
              }
            }
            // Other ground-targeted skills (Leap, Teleport) can target any empty tile
            else if (skillName !== 'Shadow Step' && !unitAtPosition && tile && tile.isPassable) {
              targets.push({ x, y });
              // Add to range indicator
              if (distance > 0) {
                rangePositions.push({ x, y });
              }
            }
          }
          // Swap skills can target any unit (ally or enemy)
          else if (isSwapSkill) {
            const unitAtPosition = getUnitAtPosition(x, y);
            if (unitAtPosition) {
              targets.push({ x, y });
            }
          }
          // Push/Pull skills target enemies only
          else if (isPushPull) {
            const unitAtPosition = getUnitAtPosition(x, y);
            if (unitAtPosition && !unitAtPosition.isPlayerUnit) {
              targets.push({ x, y });
            }
          }
          // Normal unit-targeted skills
          else {
            const unitAtPosition = getUnitAtPosition(x, y);
            if (unitAtPosition) {
              // Healing/buff skills target allies (including self), damage skills target enemies
              if (isHealingOrBuff && unitAtPosition.isPlayerUnit) {
                targets.push({ x, y });
              } else if (!isHealingOrBuff && !unitAtPosition.isPlayerUnit) {
                targets.push({ x, y });
              }
            }
          }

          // For non-ground-target skills, add all in-range tiles to range indicator
          if (!isGroundTarget && distance > 0) {
            rangePositions.push({ x, y });
          }
        }
      }
    }

    // Set the skill range indicator
    setSkillRange(rangePositions);

    return targets;
  };

  const executeSkillDirect = async (skill: any, targetX: number, targetY: number) => {
    if (!skill || !selectedUnit) return;

    try {
      const request = {
        CasterId: selectedUnit.id,
        SkillNodeId: skill.skill.id,
        TargetX: targetX,
        TargetY: targetY
      };

      const response = await makeAuthenticatedRequest(`/battles/${battleId}/use-skill`, {
        method: 'POST',
        body: JSON.stringify(request)
      });

      if (response.ok) {
        const result = await response.json();
        setBattle(result.battleState);

        // Add battle messages to log (status effects applied, etc.)
        if (result.battleMessages && result.battleMessages.length > 0) {
          result.battleMessages.forEach((msg: string) => {
            addToBattleLog(msg);
          });
        }

        // Update selected unit reference (important: do this before clearing modes)
        const updatedUnit = result.battleState.units.find((u: any) => u.id === selectedUnit.id);
        setSelectedUnit(updatedUnit);

        // Show skill effects
        if (result.skillEffect.damage && result.skillEffect.damage > 0) {
          addFloatingText(`-${result.skillEffect.damage}`, 'damage', targetX, targetY);
          addToBattleLog(`⚔️ ${result.skillEffect.casterName} used ${result.skillEffect.skillName} dealing ${result.skillEffect.damage} damage!`);
        }

        if (result.skillEffect.healing && result.skillEffect.healing > 0) {
          addFloatingText(`+${result.skillEffect.healing}`, 'healing', targetX, targetY);
          addToBattleLog(`✨ ${result.skillEffect.casterName} used ${result.skillEffect.skillName} healing ${result.skillEffect.healing} HP!`);
        }

        // Log skill usage and cooldown info
        if (!result.skillEffect.damage && !result.skillEffect.healing) {
          // Skill with special effects (like Stealth) that don't have immediate damage/healing
          addToBattleLog(`🎯 ${result.skillEffect.casterName} used ${result.skillEffect.skillName}!`);
        }

        if (result.skillEffect.cooldownApplied > 0) {
          addToBattleLog(`⏳ ${result.skillEffect.skillName} on cooldown for ${result.skillEffect.cooldownApplied} turns`);
        }

        // Clear skill mode (but keep unit selected)
        setSkillMode(false);
        setSelectedSkill(null);
        setSkillTargets([]);
      } else {
        const error = await response.text();
        addToBattleLog(`❌ Failed to use ${skill.skill.name}: ${error}`);
      }
    } catch (error) {
      addToBattleLog(`❌ Error using skill`);
    }
  };

  const executeSkill = async (targetX: number, targetY: number) => {
    if (!selectedSkill || !selectedUnit) return;

    try {
      const request = {
        CasterId: selectedUnit.id,
        SkillNodeId: selectedSkill.skill.id,
        TargetX: targetX,
        TargetY: targetY
      };

      const response = await makeAuthenticatedRequest(`/battles/${battleId}/use-skill`, {
        method: 'POST',
        body: JSON.stringify(request)
      });

      if (response.ok) {
        const result = await response.json();
        setBattle(result.battleState);

        // Add battle messages to log (status effects applied, etc.)
        if (result.battleMessages && result.battleMessages.length > 0) {
          result.battleMessages.forEach((msg: string) => {
            addToBattleLog(msg);
          });
        }

        // Update selected unit reference (important: do this before clearing modes)
        const updatedUnit = result.battleState.units.find((u: any) => u.id === selectedUnit.id);
        setSelectedUnit(updatedUnit);

        // Show skill effects
        if (result.skillEffect.damage && result.skillEffect.damage > 0) {
          addFloatingText(`-${result.skillEffect.damage}`, 'damage', targetX, targetY);
          addToBattleLog(`⚔️ ${result.skillEffect.casterName} used ${result.skillEffect.skillName} dealing ${result.skillEffect.damage} damage!`);
        }

        if (result.skillEffect.healing && result.skillEffect.healing > 0) {
          addFloatingText(`+${result.skillEffect.healing}`, 'healing', targetX, targetY);
          addToBattleLog(`✨ ${result.skillEffect.casterName} used ${result.skillEffect.skillName} healing ${result.skillEffect.healing} HP!`);
        }

        // Log skill usage and cooldown info
        if (!result.skillEffect.damage && !result.skillEffect.healing) {
          // Skill with special effects (like Stealth) that don't have immediate damage/healing
          addToBattleLog(`🎯 ${result.skillEffect.casterName} used ${result.skillEffect.skillName}!`);
        }

        if (result.skillEffect.cooldownApplied > 0) {
          addToBattleLog(`⏳ ${result.skillEffect.skillName} on cooldown for ${result.skillEffect.cooldownApplied} turns`);
        }

        // Reset skill mode (but keep unit selected)
        cancelSkill();

        // Check for victory
        if (result.battleState.status === 'Victory') {
          const experience = result.skillEffect.experience || 0;
          const loot = result.skillEffect.materialsAwarded || [];
          const tilesUnlocked = result.skillEffect.tilesUnlocked || [];
          const bonuses = result.skillEffect.bonuses || null;
          const characterXP = result.skillEffect.characterXP || [];
          setVictoryData({ experience, loot, tilesUnlocked, bonuses, characterXP });
          setShowVictoryScreen(true);
        }
      } else {
        const error = await response.json();
        setActionError(error.error || 'Failed to use skill');
        console.error('Failed to use skill:', error);
      }
    } catch (err) {
      console.error('Error using skill:', err);
      setActionError('An error occurred while using the skill');
    }
  };

  const moveUnit = async (unitId: number, targetX: number, targetY: number) => {
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/move`, {
        method: 'POST',
        body: JSON.stringify({
          UnitId: unitId,
          TargetX: targetX,
          TargetY: targetY
        })
      });

      if (response.ok) {
        const result = await response.json();

        // Handle opportunity attacks
        if (result.opportunityAttacks && result.opportunityAttacks.length > 0) {
          result.opportunityAttacks.forEach((oa: any) => {
            // Add opportunity attack to battle log with special formatting
            if (oa.type === 'Grapple') {
              if (oa.grappleSuccess) {
                addToBattleLog(`💥 ${oa.message}`);
                addNotification({
                  type: 'info',
                  title: 'Grappled!',
                  message: `${oa.attackerName} grappled your unit!`
                });
              } else {
                addToBattleLog(`🏃 ${oa.message}`);
              }
            } else {
              addToBattleLog(`⚔️ ${oa.message}`);
              if (oa.damage > 0) {
                addNotification({
                  type: 'info',
                  title: 'Opportunity Attack',
                  message: `${oa.attackerName} dealt ${oa.damage} damage!`
                });
              }
            }
          });
        }

        // Check if movement was stopped by grapple
        if (result.grappled) {
          addNotification({
            type: 'info',
            title: 'Movement Stopped',
            message: 'Your unit was grappled!'
          });

          // Auto-end turn if grappled
          if (result.autoEndTurn) {
            addToBattleLog(`Turn ended automatically due to grapple`);
            const currentUnit = getCurrentTurnUnit();
            if (currentUnit) {
              // Automatically call endTurn after a short delay so player can see what happened
              setTimeout(() => {
                endTurn(currentUnit).catch(error => {
                  console.error('Error auto-ending turn after grapple:', error);
                });
              }, 1000);
            }
          }
        }

        const updatedBattle = result.battleState || result;
        setBattle(updatedBattle);

        // Check if unit died during movement
        if (result.unitDied) {
          // Unit died from opportunity attack - clear selection and exit movement mode
          setSelectedUnit(null);
          setMovementMode(false);
          setValidMoves([]);
          setActionError(null);
          addToBattleLog(`💀 ${selectedUnit.name} was killed by opportunity attack!`);
          addNotification({
            type: 'info',
            title: 'Unit Killed',
            message: `${selectedUnit.name} died from opportunity attack!`
          });
          return; // Don't try to update/log for dead unit
        }

        // Update selected unit reference
        const updatedUnit = updatedBattle.units.find((u: any) => u.id === unitId);
        setSelectedUnit(updatedUnit);
        setActionError(null); // Clear any previous action errors

        // Log the movement (only if unit is still alive)
        if (updatedUnit) {
          const distance = Math.abs(targetX - selectedUnit.position.x) + Math.abs(targetY - selectedUnit.position.y);
          addEnhancedBattleLog('unit_move', {
            unitName: updatedUnit.name,
            distance: distance
          });
        }

        // Always exit movement mode after moving (user must click Move again to continue)
        setMovementMode(false);
        setValidMoves([]);
      } else {
        const errorData = await response.json();
        setActionError(errorData.error || 'Failed to move unit');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setActionError(apiError.message);
    }
  };

  const attackUnit = async (attackerId: number, targetId: number) => {
    try {
      // Log turn state BEFORE attack
      const preAttackTurnUnit = getCurrentTurnUnit();
      
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/attack`, {
        method: 'POST',
        body: JSON.stringify({
          AttackerId: attackerId,
          TargetId: targetId
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Process passive ability events
        if (result.passiveEvents && result.passiveEvents.length > 0) {
          processPassiveEvents(result.passiveEvents);
        }
        
        setBattle(result.battleState);
        setAttackMode(false);
        setValidTargets([]);
        setWeaponRange([]); // Clear weapon range after attack

        // Log turn state AFTER attack
        
        // Update selected unit reference  
        const updatedUnit = result.battleState.units.find((u: any) => u.id === attackerId);
        setSelectedUnit(updatedUnit);
        
        // Clear any previous errors since attack was successful
        setActionError(null);
        
        // Log the attack result
        const { unitsHit, battleStatus, xpGained, materialsAwarded } = result.attackResult;
        const attackerName = updatedUnit.name;

        // Find the primary target in unitsHit array
        const primaryTargetHit = unitsHit?.find((hit: any) => hit.unitId === targetId);
        const damage = primaryTargetHit?.damage || 0;
        const targetDefeated = primaryTargetHit?.defeated || false;

        const targetUnit = battle.units.find((u: any) => u.id === targetId);
        const targetName = targetUnit?.name || primaryTargetHit?.unitName || 'Unknown';

        // Enhanced attack logging with health status
        const targetRemainingHP = targetDefeated ? 0 : (targetUnit ? targetUnit.stats.currentHP : 0);
        const targetMaxHP = targetUnit ? targetUnit.stats.maxHP : 100;

        addEnhancedBattleLog('attack', {
          attackerName: attackerName,
          targetName: targetName,
          damage: damage,
          targetDefeated: targetDefeated,
          targetRemainingHP: targetRemainingHP,
          targetMaxHP: targetMaxHP
        });
        
        // Show XP gain only if significant (reduce XP spam)
        if (xpGained && xpGained >= 10) {
          addEnhancedBattleLog('xp_gain', {
            unitName: attackerName,
            xpAmount: xpGained
          });
        }
        
        // Check for battle end

        if (battleStatus === 'Victory') {
          addEnhancedBattleLog('victory', {});

          // Show achievement notifications from backend
          if (result.attackResult.achievementsUnlocked && result.attackResult.achievementsUnlocked.length > 0) {
            result.attackResult.achievementsUnlocked.forEach((achievement: any) => {
              addNotification({
                type: 'achievement',
                title: `${achievement.icon} Achievement Unlocked!`,
                message: `${achievement.name} - ${achievement.description} (${achievement.currentProgress}/${achievement.requiredValue})`
              });
            });
          }

          // Prepare victory screen data
          const loot = materialsAwarded || [];
          const experience = xpGained || 0;
          const tilesUnlocked = result.attackResult.tilesUnlocked || [];
          const bonuses = result.attackResult.bonuses || null;
          const characterXP = result.attackResult.characterXP || [];
          setVictoryData({ experience, loot, tilesUnlocked, bonuses, characterXP });
          setShowVictoryScreen(true);

          // Show materials awarded
          if (materialsAwarded && materialsAwarded.length > 0) {
            addEnhancedBattleLog('materials_found', { materials: materialsAwarded });

            // Trigger notifications for rare/epic loot
            materialsAwarded.forEach((material: any) => {
              const rarity = material.rarity || 'Common';
              if (rarity === 'Rare' || rarity === 'Epic' || rarity === 'Legendary') {
                addNotification({
                  type: 'loot',
                  title: `${rarity} Loot Found!`,
                  message: `You found ${material.quantity}x ${material.materialName}!`
                });
              }
            });
          }

          // Show recipes discovered
          if (result.attackResult.recipesDiscovered && result.attackResult.recipesDiscovered.length > 0) {
            addEnhancedBattleLog('recipes_discovered', { recipes: result.attackResult.recipesDiscovered });

            // Trigger notifications for recipe discoveries
            result.attackResult.recipesDiscovered.forEach((recipe: any) => {
              addNotification({
                type: 'quest',
                title: 'New Recipe Discovered!',
                message: `You learned how to craft: ${recipe.recipeName}`
              });
            });
          }
        } else if (battleStatus === 'Defeat') {
          addEnhancedBattleLog('defeat', {});
        }
        
        // Check if turn unit changed unexpectedly after attack
        setTimeout(() => {
          const postAttackTurnUnit = getCurrentTurnUnit();
          
          if (preAttackTurnUnit && postAttackTurnUnit && preAttackTurnUnit.unitId !== postAttackTurnUnit.unitId) {
          }
        }, 100);
      } else {
        const errorData = await response.json();
        setActionError(errorData.error || 'Failed to attack');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setActionError(apiError.message);
    }
  };

  const endTurn = async (unitOverride?: any) => {
    
    // Prevent multiple simultaneous turn processing
    if (isProcessingTurn) {
      return;
    }
    
    setIsProcessingTurn(true);

    // Clear all UI state immediately when ending turn
    setMovementMode(false);
    setAttackMode(false);
    setSkillMode(false);
    setValidMoves([]);
    setValidTargets([]);
    setSkillTargets([]);
    setSkillRange([]);
    setWeaponRange([]);
    setSelectedSkill(null);
    setAoeArea([]);

    // Allow endTurn to be called for both player and AI units
    // AI units will be processed automatically by the backend
    const currentTurnUnit = unitOverride || getCurrentTurnUnit();

    if (!currentTurnUnit) {
      console.warn("No current unit found in endTurn, but proceeding anyway for AI processing");
      // Don't return early - let the backend handle the turn advancement
    } else {
    }

    try {

      const response = await makeAuthenticatedRequest(`/battles/${battleId}/end-turn`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();

        // Add battle messages to log (DoT damage, status effects, etc.)
        if (result.battleMessages && result.battleMessages.length > 0) {
          result.battleMessages.forEach((msg: string) => {
            addToBattleLog(msg);
          });
        }

        // Don't immediately update battle state if there are AI actions to process

        if (result.aiActions && result.aiActions.length > 0) {
          // Store the final battle state for after animations
          const finalBattleState = result.battleState;

          setSelectedUnit(null);

          // Log turn advancement and show transition banner
          if (result.battleState?.units) {
            const nextTurnUnit = result.battleState.turnOrder[result.battleState.currentUnitIndex];
            if (nextTurnUnit) {
              // Show turn transition banner for all units
              showTurnTransition(nextTurnUnit.name, nextTurnUnit.isPlayerUnit);

              // Log only for player units to reduce clutter
              if (nextTurnUnit.isPlayerUnit) {
                addEnhancedBattleLog('turn_start', {
                  unitName: nextTurnUnit.name,
                  isPlayerUnit: nextTurnUnit.isPlayerUnit,
                  turnNumber: result.battleState.currentTurn
                });
              }
            }
          }
          
          // Process AI actions with animation, then update to final state
          setTimeout(() => {
            processAIActions(result.aiActions, () => {
              // Update to final battle state after all animations complete
              setBattle(finalBattleState);

              // Manually refresh battle state to ensure resource reset
              setTimeout(() => {
                fetchBattleState().then(() => {
                  // Check if next turn is AI and automatically continue (only once)
                  const currentTurnUnit = getCurrentTurnUnit();
                  if (currentTurnUnit && !currentTurnUnit.isPlayerUnit && battle?.status === 'InProgress' && !isProcessingTurn) {
                    // Process immediately to prevent state timing issues
                    endTurn(currentTurnUnit);
                  }
                });
              }, 1000);
            });
          }, 500);
        } else {
          // No AI actions, update immediately
          setBattle(result.battleState);
          setSelectedUnit(null);

          // Turn advancement logging handled by enhanced system above
          
          // Refresh battle state to ensure resource reset
          setTimeout(() => {
            fetchBattleState().then(() => {
              // Check if next turn is AI and automatically continue (only once)
              const currentTurnUnit = getCurrentTurnUnit();
              
              // IMPORTANT: Only auto-continue if this was an intentional turn end, not a battle state refresh
              // This prevents auto-ending player turns after attacks that kill enemies
              if (currentTurnUnit && !currentTurnUnit.isPlayerUnit && battle?.status === 'InProgress' && !isProcessingTurn) {
                // Process immediately to prevent state timing issues
                endTurn(currentTurnUnit);
              } else if (currentTurnUnit && currentTurnUnit.isPlayerUnit) {
              }
            });
          }, 500);
        }
      } else {
        const errorData = await response.json();
        console.error('End turn failed:', response.status, errorData);
        setError(errorData.error || 'Failed to end turn');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setIsProcessingTurn(false);
    }
  };

  const processAIActions = useCallback((aiActions: any[], onComplete?: () => void) => {
    
    let totalDelay = 500; // Start with initial delay

    aiActions.forEach((action, actionIndex) => {
      setTimeout(() => {
        // Handle actions that might be wrapped in arrays
        let actualAction = action;
        if (Array.isArray(action) && action.length > 0) {
          actualAction = action[0]; // Extract the first action from the array
        }
        
        // Debug: Log the actual action we're processing
        addEnhancedBattleLog('debug', { message: `Processing AI Action: ${actualAction.type} by ${actualAction.unitName || actualAction.casterName || 'unknown'}` });
        
        switch (actualAction.type) {
          case 'turnStart':
            // Skip most AI turn announcements to reduce clutter

            // Update battle state to highlight the current AI unit
            setBattle((prevBattle: any) => {
              if (!prevBattle) return prevBattle;
              
              // Find the index of the current AI unit in the turn order
              const aiUnitIndex = prevBattle.turnOrder.findIndex((unit: any) => unit.unitId === action.unitId);
              
              if (aiUnitIndex !== -1) {
                return {
                  ...prevBattle,
                  currentUnitIndex: aiUnitIndex
                };
              }
              
              return prevBattle;
            });
            break;
          case 'move':
            // Handle opportunity attacks that occurred during AI movement
            if (actualAction.opportunityAttacks && actualAction.opportunityAttacks.length > 0) {
              actualAction.opportunityAttacks.forEach((oa: any) => {
                if (oa.type === 'Grapple') {
                  if (oa.grappleSuccess) {
                    addToBattleLog(`💥 ${oa.message}`);
                    addNotification({
                      type: 'loot',
                      title: 'Grapple Success!',
                      message: `Your unit grappled ${oa.targetName}!`
                    });
                  } else {
                    addToBattleLog(`🏃 ${oa.message}`);
                  }
                } else {
                  addToBattleLog(`⚔️ ${oa.message}`);
                  if (oa.damage > 0) {
                    addNotification({
                      type: 'loot',
                      title: 'Opportunity Attack',
                      message: `${oa.damage} damage to ${oa.targetName}!`
                    });
                  }
                }
              });
            }

            // Check if AI was grappled
            if (actualAction.grappled) {
              addToBattleLog(`🛑 ${actualAction.unitName}'s movement was stopped by grapple!`);
            }

            if (actualAction.path && actualAction.path.length > 0) {
              const distance = actualAction.path.length;
              addEnhancedBattleLog('unit_move', {
                unitName: actualAction.unitName,
                distance: distance
              });
              // Animate each step of the movement
              animateUnitMovement(actualAction.unitId, actualAction.path, () => {
                // Movement animation complete - no additional logging needed
              });
            }
            break;
          case 'attack':
            // Enhanced AI attack logging (using estimated values for health status)
            addEnhancedBattleLog('attack', {
              attackerName: actualAction.attackerName,
              targetName: actualAction.targetName,
              damage: actualAction.damage,
              targetDefeated: actualAction.targetDefeated,
              targetRemainingHP: actualAction.targetDefeated ? 0 : (actualAction.targetRemainingHP || 50),
              targetMaxHP: actualAction.targetMaxHP || 100
            });
            break;
          case 'cast_skill':
            // Handle AI skill casting
            addEnhancedBattleLog('skill_cast', {
              casterName: actualAction.casterName,
              skillName: actualAction.skillName,
              damage: actualAction.damage,
              healing: actualAction.healing,
              affectedCount: actualAction.affectedUnits ? actualAction.affectedUnits.length : 1
            });
            break;
          case 'battleEnd':
            if (actualAction.result === 'defeat') {
              addEnhancedBattleLog('defeat', {});
            } else if (actualAction.result === 'victory') {
              addEnhancedBattleLog('victory', {});
              
              // Show materials awarded
              if (actualAction.materialsAwarded && actualAction.materialsAwarded.length > 0) {
                addEnhancedBattleLog('materials_found', { materials: actualAction.materialsAwarded });
              }
              
              // Show recipes discovered
              if (actualAction.recipesDiscovered && actualAction.recipesDiscovered.length > 0) {
                addEnhancedBattleLog('recipes_discovered', { recipes: actualAction.recipesDiscovered });
              }
            }
            break;
          default:
            // Handle unknown action types
            
            // Try to extract meaningful info from unknown actions
            if (actualAction.damage && actualAction.attackerName && actualAction.targetName) {
              // Looks like an attack action with different structure
              addEnhancedBattleLog('attack', {
                attackerName: actualAction.attackerName,
                targetName: actualAction.targetName,
                damage: actualAction.damage,
                targetDefeated: actualAction.targetDefeated || false,
                targetRemainingHP: actualAction.targetRemainingHP || 50,
                targetMaxHP: actualAction.targetMaxHP || 100
              });
            }
            break;
        }
      }, totalDelay);

      // Calculate delay for next action using actualAction
      let currentAction = action;
      if (Array.isArray(action) && action.length > 0) {
        currentAction = action[0];
      }
      
      if (currentAction.type === 'turnStart') {
        totalDelay += 800; // Short delay for turn start
      } else if (currentAction.type === 'move' && currentAction.path) {
        totalDelay += (currentAction.path.length * 750) + 800; // 750ms per step + 800ms buffer
      } else if (currentAction.type === 'cast_skill') {
        totalDelay += 1500; // Delay for skill casting
      } else {
        totalDelay += 1200; // Standard delay for attacks
      }
    });

    // Call completion callback after all actions are done
    if (onComplete) {
      setTimeout(onComplete, totalDelay + 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBattle]);

  const animateUnitMovement = (unitId: number, path: Array<{x: number, y: number}>, onComplete: () => void) => {
    // Animate step-by-step movement with delays
    path.forEach((step, stepIndex) => {
      setTimeout(() => {
        // Update battle state to show intermediate position
        setBattle((prevBattle: any) => {
          if (!prevBattle) return prevBattle;
          
          return {
            ...prevBattle,
            units: prevBattle.units.map((unit: any) => 
              unit.id === unitId 
                ? { ...unit, position: { x: step.x, y: step.y } }
                : unit
            )
          };
        });

        // Call completion callback after last step
        if (stepIndex === path.length - 1) {
          setTimeout(onComplete, 200);
        }
      }, stepIndex * 750); // 750ms delay between each step
    });
  };

  const getCurrentTurnUnit = () => {

    if (!battle?.turnOrder || battle.turnOrder.length === 0) {
      return null;
    }

    // Use the backend's currentUnitIndex directly instead of calculating from currentTurn
    const currentIndex = battle.currentUnitIndex || 0;

    // Ensure index is within bounds
    if (currentIndex >= battle.turnOrder.length || currentIndex < 0) {
      return battle.turnOrder[0];
    }

    const currentUnit = battle.turnOrder[currentIndex];
    if (!currentUnit) {
      return battle.turnOrder[0];
    }

    return currentUnit;
  };

  const handleFleeBattle = async () => {
    try {
      if (!battle || battle.status !== 'InProgress') {
        // If battle is already over, just exit
        onExitBattle();
        return;
      }

      // Get player units in battle to calculate penalty preview
      const playerUnits = battle.units?.filter((u: any) => u.isPlayerUnit && u.characterId) || [];

      if (playerUnits.length === 0) {
        onExitBattle();
        return;
      }

      // Calculate total penalty for confirmation message
      let totalPenalty = 0;
      const penaltyList: string[] = [];

      for (const unit of playerUnits) {
        if (unit.character) {
          const currentXP = unit.character.experience || 0;
          const penalty = Math.ceil(currentXP * 0.01); // 1% rounded up
          totalPenalty += penalty;
          penaltyList.push(`${unit.name}: -${penalty} XP`);
        }
      }

      // Show confirmation dialog
      const message = `Fleeing will cost you ${totalPenalty} XP total:\n\n${penaltyList.join('\n')}\n\nAre you sure you want to flee?`;
      const confirmed = window.confirm(message);

      if (!confirmed) {
        return; // User cancelled
      }

      // Call flee endpoint
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/flee`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();

        addNotification({
          type: 'info',
          title: 'Fled Battle',
          message: `You fled the battle. Total XP penalty: ${result.totalPenalty}`
        });

        // Exit battle
        onExitBattle();
      } else {
        const errorData = await response.json();
        addNotification({
          type: 'info',
          title: 'Flee Failed',
          message: errorData.error || 'Failed to flee battle'
        });
      }
    } catch (error) {
      console.error('Error fleeing battle:', error);
      addNotification({
        type: 'info',
        title: 'Error',
        message: 'An error occurred while fleeing the battle'
      });
    }
  };


  if (loading) {
    return <div className="battle-loading">Loading battle...</div>;
  }

  if (error) {
    return (
      <div className="battle-error">
        <p>Error: {error}</p>
        <button onClick={onExitBattle}>Return to World Map</button>
      </div>
    );
  }

  if (!battle) {
    return (
      <div className="battle-error">
        <p>Battle not found</p>
        <button onClick={onExitBattle}>Return to World Map</button>
      </div>
    );
  }

  const currentTurnUnit = getCurrentTurnUnit();

  return (
    <div className="battle-screen">

      {/* Victory Screen */}
      {showVictoryScreen && battle.status === 'Victory' && (
        <BattleVictory
          experience={victoryData.experience}
          loot={victoryData.loot.map((m: any) => ({
            name: m.materialName,
            quantity: m.quantity,
            rarity: m.rarity || 'Common'
          }))}
          tilesUnlocked={victoryData.tilesUnlocked}
          bonuses={victoryData.bonuses}
          characterXP={victoryData.characterXP}
          onContinue={onExitBattle}
        />
      )}

      {/* Defeat Banner */}
      {battle.status === 'Defeat' && (
        <div className="battle-end-banner defeat" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: '#f44336',
          color: 'white',
          padding: '20px 40px',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          textAlign: 'center',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          💀 DEFEAT!
          <div style={{ fontSize: '16px', marginTop: '10px' }}>
            All units defeated!
          </div>
          <button
            onClick={onExitBattle}
            style={{
              marginTop: '15px',
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: 'white',
              color: '#f44336',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Return to World Map
          </button>
        </div>
      )}

      <div className="battle-main">
        <div
          className="battle-grid-container"
          ref={gridContainerRef}
          style={{
            width: `${gridViewportSize.width}px`,
            height: `${gridViewportSize.height}px`,
            position: 'relative',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Zoom Controls */}
          <div className="grid-zoom-controls">
            <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out (-)">-</button>
            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
            <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In (+)">+</button>
            <button className="zoom-btn reset" onClick={handleResetZoom} title="Reset View">⟲</button>
          </div>

          {/* Resize Handles */}
          <div className="resize-handle top" onMouseDown={(e) => handleResizeStart(e, 'top')} />
          <div className="resize-handle right" onMouseDown={(e) => handleResizeStart(e, 'right')} />
          <div className="resize-handle bottom" onMouseDown={(e) => handleResizeStart(e, 'bottom')} />
          <div className="resize-handle left" onMouseDown={(e) => handleResizeStart(e, 'left')} />
          <div className="resize-handle top-left" onMouseDown={(e) => handleResizeStart(e, 'top-left')} />
          <div className="resize-handle top-right" onMouseDown={(e) => handleResizeStart(e, 'top-right')} />
          <div className="resize-handle bottom-left" onMouseDown={(e) => handleResizeStart(e, 'bottom-left')} />
          <div className="resize-handle bottom-right" onMouseDown={(e) => handleResizeStart(e, 'bottom-right')} />

          <div
            className="battle-grid"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out'
            }}
          >
            {Array.from({ length: battle.gridHeight }, (_, y) => (
              <div key={y} className="grid-row">
                {Array.from({ length: battle.gridWidth }, (_, x) => {
                  const tile = getTileAtPosition(x, y);
                  const unit = getUnitAtPosition(x, y);
                  const isSelected = selectedUnit && unit && selectedUnit.id === unit.id;
                  const isValidMove = validMoves.some(move => move.x === x && move.y === y);
                  const isValidTarget = unit && validTargets.some(target => target.id === unit.id);
                  const isSkillTarget = skillTargets.some(target => target.x === x && target.y === y);
                  const isSkillRange = skillRange.some(range => range.x === x && range.y === y);
                  const isAoeArea = aoeArea.some(area => area.x === x && area.y === y);
                  const isCurrentTurnUnit = unit && currentTurnUnit && unit.id === currentTurnUnit.unitId;
                  const isWeaponRange = weaponRange.some(range => range.x === x && range.y === y);

                  const handleTileHover = () => {
                    // Show AOE preview for attack mode
                    if (attackMode && selectedUnit && isValidTarget) {
                      const weapon = selectedUnit.equipment?.weapon;
                      const aoeShape = weapon?.aoeShape || 'Single';
                      const aoeTiles = calculateAOEPreview(selectedUnit.position, x, y, aoeShape);
                      setAoeArea(aoeTiles);
                    }
                    // Show AoE preview for skills when hovering over valid target
                    if (skillMode && selectedSkill && isSkillTarget) {
                      const skill = selectedSkill.skill;
                      const aoe = skill?.areaOfEffect || 0;

                      if (aoe > 0) {
                        // Calculate AOE tiles based on radius
                        const aoeTiles: {x: number, y: number}[] = [{x, y}]; // Center tile

                        // Add all tiles within AOE radius
                        for (let dx = -aoe; dx <= aoe; dx++) {
                          for (let dy = -aoe; dy <= aoe; dy++) {
                            if (dx === 0 && dy === 0) continue; // Skip center (already added)
                            const distance = Math.abs(dx) + Math.abs(dy); // Manhattan distance
                            if (distance <= aoe) {
                              aoeTiles.push({x: x + dx, y: y + dy});
                            }
                          }
                        }
                        setAoeArea(aoeTiles);
                      } else {
                        setAoeArea([{x, y}]); // Single target
                      }
                    }
                  };

                  const handleTileLeave = () => {
                    if (skillMode || attackMode) {
                      setAoeArea([]);
                    }
                  };

                  // Generate detailed tile tooltip with terrain effects
                  const getTileTooltip = () => {
                    if (!tile) return `(${x},${y})`;
                    let tooltip = `${tile.type} (${x},${y})`;

                    if (tile.movementCost > 1) {
                      tooltip += `\nMovement Cost: ${tile.movementCost}`;
                    }
                    if (tile.evasionBonus > 0) {
                      tooltip += `\n+${tile.evasionBonus}% Evasion`;
                    } else if (tile.evasionBonus < 0) {
                      tooltip += `\n${tile.evasionBonus}% Evasion`;
                    }
                    if (tile.defenseBonus > 0) {
                      tooltip += `\n+${tile.defenseBonus}% Defense`;
                    }
                    if (tile.hazardType) {
                      tooltip += `\n⚠️ ${tile.hazardType} Hazard (${tile.hazardDamage} dmg/turn)`;
                    }
                    if (!tile.isPassable) {
                      tooltip += `\nImpassable`;
                    }
                    if (tile.blocksLineOfSight) {
                      tooltip += `\nBlocks Vision`;
                    }
                    return tooltip;
                  };

                  return (
                    <div
                      key={`${x}-${y}`}
                      className={`grid-tile ${isSelected ? 'selected' : ''} ${unit ? 'has-unit' : ''} ${isCurrentTurnUnit ? 'current-turn-unit' : ''} ${isValidMove ? 'valid-move' : ''} ${isValidTarget ? 'valid-target' : ''} ${isSkillTarget ? 'skill-target' : ''} ${isSkillRange ? 'skill-range' : ''} ${isAoeArea ? 'aoe-area' : ''} ${isWeaponRange ? 'weapon-range' : ''} ${tile?.hazardType ? 'hazard-tile' : ''}`}
                      style={{ backgroundColor: getTileColor(tile) }}
                      onClick={() => handleTileClick(x, y)}
                      onMouseEnter={handleTileHover}
                      onMouseLeave={handleTileLeave}
                      title={getTileTooltip()}
                    >
                      <div className="tile-coords">{x},{y}</div>
                      {tile?.hazardType && (
                        <div className="hazard-indicator" title={`${tile.hazardType} Hazard: ${tile.hazardDamage} damage/turn`}>
                          ⚠️
                        </div>
                      )}
                      {unit && (
                        <div className={`unit ${unit.isPlayerUnit ? 'player-unit' : 'enemy-unit'}`}>
                          {unit.monster && unit.monster.imageUrl && 
                           !unit.monster.imageUrl.includes('example.com') && (
                            <div className="unit-image">
                              <img 
                                src={unit.monster.imageUrl} 
                                alt={unit.monster.name}
                                className="monster-image"
                                onError={(e) => {
                                  // Hide image on error and log for debugging
                                  console.warn(`Failed to load monster image: ${unit.monster.imageUrl}`);
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="unit-name">{unit.name}</div>
                          <div className="unit-hp">{unit.stats.currentHP}/{unit.stats.maxHP}</div>
                          {/* Status Effect Icons */}
                          {unit.statusEffects && unit.statusEffects.length > 0 && (
                            <div className="unit-status-icons">
                              {unit.statusEffects.slice(0, 3).map((effect: any, idx: number) => {
                                const getEffectIcon = (effectType: string): string => {
                                  switch (effectType) {
                                    case 'Poison': return '🧪';
                                    case 'Burn': return '🔥';
                                    case 'Invisibility': return '👻';
                                    case 'Root': return '🌿';
                                    case 'DamageBuff': return '⚔️';
                                    case 'DamageReduction': return '🛡️';
                                    case 'StatBuff': return '✨';
                                    case 'Stun': return '💫';
                                    default: return '❓';
                                  }
                                };
                                return (
                                  <span key={idx} className="status-icon" title={`${effect.effectName} (${effect.turnsRemaining} turns)`}>
                                    {getEffectIcon(effect.effectType)}
                                  </span>
                                );
                              })}
                              {unit.statusEffects.length > 3 && (
                                <span className="status-icon-more" title={`+${unit.statusEffects.length - 3} more effects`}>
                                  +{unit.statusEffects.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          <BattleUnitEquipment
                            equipment={unit.equipment || {}}
                            isPlayerUnit={unit.isPlayerUnit}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Actions Bar - 10 horizontal slots */}
          {selectedUnit && selectedUnit.isPlayerUnit && (
            <div className={`actions-bar ${!currentTurnUnit || currentTurnUnit.unitId !== selectedUnit.id ? 'not-current-turn' : ''}`}>
              {/* Slot 1: Attack */}
              <div
                className={`action-slot ${attackMode ? 'active' : ''} ${!selectedUnit.equipment?.weapon || selectedUnit.stats.currentAP < (selectedUnit.equipment?.weapon?.weaponAPCost || 2) ? 'unavailable' : ''}`}
                onClick={() => {
                  // Only allow actions if it's this unit's turn
                  if (!currentTurnUnit || currentTurnUnit.unitId !== selectedUnit.id) return;
                  if (!selectedUnit.equipment?.weapon) return;
                  const weaponAPCost = selectedUnit.equipment.weapon.weaponAPCost || 2;
                  if (selectedUnit.stats.currentAP < weaponAPCost) return; // Not enough AP
                  enterAttackMode();
                }}
                onMouseEnter={() => {
                  if (selectedUnit.equipment?.weapon && !attackMode) {
                    setWeaponRange(calculateWeaponRange(selectedUnit));
                  }
                }}
                onMouseLeave={() => {
                  if (!attackMode) {
                    setWeaponRange([]);
                  }
                }}
                title={selectedUnit.equipment?.weapon
                  ? `Attack with ${selectedUnit.equipment.weapon.itemName}\nRange: ${selectedUnit.equipment.weapon.minRange}-${selectedUnit.equipment.weapon.maxRange}\nDamage: ${selectedUnit.equipment.weapon.minDamage}-${selectedUnit.equipment.weapon.maxDamage}\nAP Cost: ${selectedUnit.equipment.weapon.weaponAPCost || 2}`
                  : 'No weapon equipped'}
              >
                <div className="action-slot-number">`</div>
                <div className="action-slot-icon">⚔️</div>
                <div className="action-slot-name">Attack</div>
                {selectedUnit.equipment?.weapon && (
                  <div className="action-slot-cost">{selectedUnit.equipment.weapon.weaponAPCost || 2} AP</div>
                )}
              </div>

              {/* Skills 1-10: Equipped Skills */}
              {Array.from({ length: 10 }, (_, index) => {
                const displayKey = index === 9 ? '0' : (index + 1).toString();
                const equippedSkill = equippedSkills.find(es => es.hotbarSlot === index);

                if (!equippedSkill) {
                  // Empty slot
                  return (
                    <div key={index} className="action-slot empty" title="Empty slot - Configure in Character Skills screen">
                      <div className="action-slot-number">{displayKey}</div>
                      <div className="action-slot-empty-icon">+</div>
                    </div>
                  );
                }

                const skill = equippedSkill.skill;
                const cooldownInfo = selectedUnit.skillCooldowns?.find(
                  (cd: any) => cd.skillNodeId === skill.id
                );
                const isOnCooldown = cooldownInfo && cooldownInfo.turnsRemaining > 0;
                const stunned = isUnitStunned(selectedUnit);
                const canAfford = selectedUnit.stats.currentAP >= (skill.apCost || 0);
                const isUnavailable = !canAfford || isOnCooldown || stunned;
                const isActive = skillMode && selectedSkill && selectedSkill.skill.id === skill.id;

                return (
                  <div
                    key={index}
                    className={`action-slot ${isActive ? 'active' : ''} ${isUnavailable ? 'unavailable' : ''}`}
                    onClick={() => {
                      // Only allow actions if it's this unit's turn
                      if (!currentTurnUnit || currentTurnUnit.unitId !== selectedUnit.id) return;
                      if (isUnavailable) return;
                      selectSkillNode(equippedSkill);
                    }}
                    onMouseEnter={() => {
                      if (!isUnavailable && !skillMode) {
                        // Show range indicator on hover
                        const targets = calculateSkillNodeTargets(skill);
                        setSkillRange(targets);
                      }
                    }}
                    onMouseLeave={() => {
                      if (!skillMode) {
                        setSkillRange([]);
                      }
                    }}
                    title={
                      stunned ? 'Cannot use skills while stunned' :
                      isOnCooldown ? `${skill.name}\n${skill.description}\n\nOn Cooldown: ${cooldownInfo.turnsRemaining}/${cooldownInfo.totalCooldown} turns` :
                      `${skill.name}${skill.skillLevel ? ` (Level ${skill.skillLevel})` : ''}\n${skill.description}\n\nAP Cost: ${skill.apCost || 0}\nRange: ${skill.minRange || 0}-${skill.maxRange || 1}${skill.areaOfEffect > 0 ? `\nAOE: ${skill.areaOfEffect}` : ''}\nCooldown: ${skill.cooldown || 0} turns${skill.skillXP !== undefined && skill.xpToNextLevel ? `\n\nProgress: ${skill.skillXP}/${skill.xpToNextLevel} XP\nTimes Used: ${skill.timesUsed || 0}` : ''}`
                    }
                  >
                    <div className="action-slot-number">{displayKey}</div>
                    <div className="action-slot-icon">{skill.icon || '✨'}</div>
                    <div className="action-slot-name">
                      {skill.name}
                      {skill.skillLevel && skill.skillLevel > 1 && (
                        <span className="skill-level-badge">Lv{skill.skillLevel}</span>
                      )}
                    </div>
                    <div className="action-slot-cost">{skill.apCost || 0} AP</div>
                    {isOnCooldown && (
                      <div className="action-slot-cooldown">{cooldownInfo.turnsRemaining}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Turn Order - below actions bar */}
          <div className="turn-order-section">
            <h3 className="turn-order-label">Turn Order</h3>
            <div className="turn-list-horizontal">
              {battle.turnOrder?.map((turnUnit: any, index: number) => {
                const isCurrent = currentTurnUnit && turnUnit.unitId === currentTurnUnit.unitId;
                // Find the full unit data to get stats
                const fullUnit = battle.units?.find((u: any) => u.id === turnUnit.unitId);

                return (
                  <div
                    key={turnUnit.unitId}
                    className={`turn-item-horizontal ${isCurrent ? 'current-turn' : ''} ${turnUnit.isPlayerUnit ? 'player-unit' : 'enemy-unit'} ${
                      turnUnit.isPlayerUnit ||
                      (!turnUnit.isPlayerUnit && (attackMode || skillMode))
                        ? 'clickable'
                        : ''
                    }`}
                    onClick={() => {
                      // When in targeting mode, use turn cards for targeting
                      if (attackMode || skillMode) {
                        // If an enemy unit and we're in attack mode, attack them
                        if (!turnUnit.isPlayerUnit && attackMode && selectedUnit && fullUnit) {
                          if (validTargets.some(target => target.id === fullUnit.id)) {
                            attackUnit(selectedUnit.id, fullUnit.id);
                          }
                          return;
                        }

                        // If in skill mode, execute skill on any valid target (enemy or ally)
                        if (skillMode && selectedSkill && selectedUnit && fullUnit) {
                          if (skillTargets.some(target => target.x === fullUnit.position.x && target.y === fullUnit.position.y)) {
                            executeSkill(fullUnit.position.x, fullUnit.position.y);
                          }
                          return;
                        }
                      }

                      // When NOT in targeting mode, clicking any player unit selects them to view their action bar
                      if (turnUnit.isPlayerUnit && fullUnit) {
                        setSelectedUnit(fullUnit);
                        // Clear any active modes
                        setMovementMode(false);
                        setAttackMode(false);
                        setSkillMode(false);
                        setValidMoves([]);
                        setValidTargets([]);
                        setSkillTargets([]);
                        setWeaponRange([]);
                        setSkillRange([]);
                        return;
                      }
                    }}
                    style={{ cursor:
                      turnUnit.isPlayerUnit || (!turnUnit.isPlayerUnit && (attackMode || skillMode))
                        ? 'pointer'
                        : 'default'
                    }}
                    title={
                      attackMode && !turnUnit.isPlayerUnit
                        ? 'Click to attack'
                        : skillMode && !turnUnit.isPlayerUnit
                        ? 'Click to cast skill'
                        : skillMode && turnUnit.isPlayerUnit && fullUnit && skillTargets.some(t => t.x === fullUnit.position.x && t.y === fullUnit.position.y)
                        ? 'Click to cast skill'
                        : turnUnit.isPlayerUnit
                        ? 'Click to view action bar'
                        : ''
                    }
                  >
                    <div className="turn-unit-header">
                      <span className="turn-name-horizontal">{turnUnit.name}</span>
                      <span className="turn-initiative-horizontal">{turnUnit.initiative}</span>
                    </div>

                    {fullUnit && (
                      <div className="turn-unit-stats">
                        {/* HP Bar with Numbers */}
                        <div className="stat-bar hp-bar">
                          <div className="stat-label">HP</div>
                          <div className="stat-bar-container">
                            <div className="stat-bar-bg">
                              <div
                                className="stat-bar-fill hp-fill"
                                style={{
                                  width: `${Math.max(0, (fullUnit.stats.currentHP / fullUnit.stats.maxHP) * 100)}%`,
                                  backgroundColor: getHealthColor(fullUnit.stats.currentHP / fullUnit.stats.maxHP)
                                }}
                              ></div>
                            </div>
                            <span className="stat-text">{fullUnit.stats.currentHP}/{fullUnit.stats.maxHP}</span>
                          </div>
                        </div>

                        {/* Status Effects Icons */}
                        {fullUnit.statusEffects && fullUnit.statusEffects.length > 0 && (
                          <div className="turn-status-effects">
                            {fullUnit.statusEffects.map((effect: any, idx: number) => (
                              <span key={idx} className="status-effect-icon" title={`${effect.type} (${effect.duration} turns)`}>
                                {effect.type === 'Poison' ? '☠️' :
                                 effect.type === 'Burn' ? '🔥' :
                                 effect.type === 'Regen' ? '💚' :
                                 effect.type === 'Shield' ? '🛡️' :
                                 effect.type === 'Stun' ? '💫' :
                                 effect.type === 'Slow' ? '🐌' :
                                 effect.type === 'Haste' ? '⚡' :
                                 effect.type === 'Blind' ? '👁️' :
                                 effect.type === 'Weaken' ? '⬇️' :
                                 effect.type === 'Strengthen' ? '⬆️' : '✨'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {battle?.status === 'InProgress' && currentTurnUnit && (
              <div className={`turn-timer ${turnTimeRemaining <= 10 ? 'timer-warning' : ''}`}>
                <span className="timer-icon">⏱️</span>
                <span className="timer-text">{turnTimeRemaining}s</span>
              </div>
            )}
          </div>

          {/* Battle Effects for passive abilities and floating text */}
          <BattleEffects
            floatingTexts={floatingTexts}
            passiveNotifications={passiveNotifications}
            onFloatingTextComplete={removeFloatingText}
            onPassiveNotificationComplete={removePassiveNotification}
          />

          {/* Battle Log - positioned directly below the battle grid */}
          <div className="battle-log-bottom">
            <div className="battle-log-header">
              <h3>Battle Log</h3>
              <div className="battle-log-filters">
                <button
                  className={`filter-btn ${battleLogFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setBattleLogFilter('all')}
                >
                  All
                </button>
                <button
                  className={`filter-btn ${battleLogFilter === 'combat' ? 'active' : ''}`}
                  onClick={() => setBattleLogFilter('combat')}
                >
                  Combat
                </button>
                <button
                  className={`filter-btn ${battleLogFilter === 'movement' ? 'active' : ''}`}
                  onClick={() => setBattleLogFilter('movement')}
                >
                  Movement
                </button>
                <button
                  className={`filter-btn ${battleLogFilter === 'important' ? 'active' : ''}`}
                  onClick={() => setBattleLogFilter('important')}
                >
                  Important
                </button>
              </div>
            </div>
            <div className="log-content" ref={logContentRef}>
              {getFilteredBattleLog().map((entry, index) => (
                <div key={index} className="log-entry">
                  {entry}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="battle-sidebar">
          {/* Battle Name */}
          <div className="battle-sidebar-header">
            <h4>{battle.battleName}</h4>
          </div>

          {(() => {
            // Show details for selectedUnit if available, otherwise show current turn unit
            const currentTurnUnit = getCurrentTurnUnit();
            const displayUnit = selectedUnit || (currentTurnUnit ? battle?.units?.find((u: any) => u.id === currentTurnUnit.unitId) : null);

            return displayUnit ? (
              <div className="unit-details">
                <h3>Unit Details {!selectedUnit && currentTurnUnit ? '(Current Turn)' : ''}</h3>
                <div className="unit-info">
                  <h4>{displayUnit.name}</h4>
                  <p><strong>Type:</strong> {displayUnit.type}</p>
                  {displayUnit.race && <p><strong>Race:</strong> {displayUnit.race}</p>}
                  {displayUnit.characterClass && <p><strong>Class:</strong> {displayUnit.characterClass}</p>}
                  {displayUnit.enemyType && <p><strong>Enemy Type:</strong> {displayUnit.enemyType}</p>}
                  <p><strong>Position:</strong> ({displayUnit.position.x}, {displayUnit.position.y})</p>
                
                <div className="unit-stats">
                  <div style={{ marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ minWidth: '40px', fontWeight: 'bold', color: 'white' }}>HP:</span>
                      <div style={{ flex: 1, height: '20px', backgroundColor: '#333', border: '1px solid #555', borderRadius: '4px', position: 'relative' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.min(100, displayUnit.stats?.maxHP > 0 ? (displayUnit.stats.currentHP / displayUnit.stats.maxHP) * 100 : 0)}%`,
                            backgroundColor: '#4CAF50',
                            borderRadius: '3px'
                          }}
                        />
                        <div style={{ position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <span style={{ color: 'white', fontSize: '11px', fontWeight: 'bold', textShadow: '1px 1px 1px black' }}>
                            {displayUnit.stats?.currentHP || 0}/{displayUnit.stats?.maxHP || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ minWidth: '40px', fontWeight: 'bold', color: 'white' }}>AP:</span>
                      <div style={{ flex: 1, height: '20px', backgroundColor: '#333', border: '1px solid #555', borderRadius: '4px', position: 'relative' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.min(100, displayUnit.stats?.maxAP > 0 ? (displayUnit.stats.currentAP / displayUnit.stats.maxAP) * 100 : 0)}%`,
                            backgroundColor: '#FF9800',
                            borderRadius: '3px'
                          }}
                        />
                        <div style={{ position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <span style={{ color: 'white', fontSize: '11px', fontWeight: 'bold', textShadow: '1px 1px 1px black' }}>
                            {displayUnit.stats?.currentAP || 0}/{displayUnit.stats?.maxAP || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ minWidth: '40px', fontWeight: 'bold', color: 'white' }}>Move:</span>
                      <div style={{ flex: 1, height: '20px', backgroundColor: '#333', border: '1px solid #555', borderRadius: '4px', position: 'relative' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.min(100, displayUnit.stats?.maxMovement > 0 ? (displayUnit.stats.currentMovement / displayUnit.stats.maxMovement) * 100 : 0)}%`,
                            backgroundColor: '#9C27B0',
                            borderRadius: '3px'
                          }}
                        />
                        <div style={{ position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <span style={{ color: 'white', fontSize: '11px', fontWeight: 'bold', textShadow: '1px 1px 1px black' }}>
                            {displayUnit.stats?.currentMovement || 0}/{displayUnit.stats?.maxMovement || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weapon Info */}
                {displayUnit.equipment?.weapon && (
                  <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)', borderRadius: '6px' }}>
                    <p style={{ margin: '4px 0', fontWeight: 'bold', color: '#FF9800' }}>
                      ⚔️ {displayUnit.equipment.weapon.itemName || 'Weapon'}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '12px' }}>
                      <strong>Damage:</strong> {displayUnit.equipment.weapon.minDamage}-{displayUnit.equipment.weapon.maxDamage} ({displayUnit.equipment.weapon.damageType || 'Physical'})
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '12px' }}>
                      <strong>Range:</strong> {displayUnit.equipment.weapon.minRange}-{displayUnit.equipment.weapon.maxRange}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '12px' }}>
                      <strong>AP Cost:</strong> {displayUnit.equipment.weapon.weaponAPCost || 1}
                    </p>
                    {displayUnit.equipment.weapon.aoeShape && displayUnit.equipment.weapon.aoeShape !== 'Single' && (
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#FF5722' }}>
                        <strong>AOE:</strong> {displayUnit.equipment.weapon.aoeShape}
                      </p>
                    )}
                    {displayUnit.equipment.weapon.usesAmmo && (
                      <p style={{ margin: '4px 0', fontSize: '12px', color: displayUnit.equipment.weapon.currentAmmo === 0 ? '#F44336' : '#FFC107' }}>
                        <strong>Ammo:</strong> {displayUnit.equipment.weapon.currentAmmo}/{displayUnit.equipment.weapon.maxAmmo}
                      </p>
                    )}
                  </div>
                )}

                <p><strong>Initiative:</strong> {displayUnit.initiative}</p>
                <p><strong>Has Acted:</strong> {displayUnit.hasActedThisTurn ? 'Yes' : 'No'}</p>
                <p><strong>Has Moved:</strong> {displayUnit.hasMovedThisTurn ? 'Yes' : 'No'}</p>

                {/* Status Effects Display */}
                {displayUnit.statusEffects && displayUnit.statusEffects.length > 0 && (
                  <div className="status-effects-container">
                    <h4 style={{ marginTop: '15px', marginBottom: '8px', color: 'white' }}>Active Effects</h4>
                    <div className="status-effects-list">
                      {displayUnit.statusEffects.map((effect: any) => {
                        const getEffectIcon = (effectType: string): string => {
                          switch (effectType) {
                            case 'Poison': return '🧪';
                            case 'Burn': return '🔥';
                            case 'Invisibility': return '👻';
                            case 'Root': return '🌿';
                            case 'DamageBuff': return '⚔️';
                            case 'DamageReduction': return '🛡️';
                            case 'StatBuff': return '✨';
                            case 'Stun': return '💫';
                            default: return '📋';
                          }
                        };

                        return (
                          <div
                            key={effect.id}
                            className={`status-effect status-effect-${effect.effectType.toLowerCase()}`}
                            title={`${effect.effectName}: ${effect.turnsRemaining}/${effect.totalDuration} turns remaining`}
                          >
                            <span className="effect-icon">{getEffectIcon(effect.effectType)}</span>
                            <span className="effect-name">{effect.effectName}</span>
                            <span className="effect-turns">{effect.turnsRemaining}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            ) : null;
          })()}

          <div className="battle-actions">
            <h3>Actions</h3>
            {movementMode ? (
              <>
                <button className="action-btn cancel-btn" onClick={cancelMovement}>
                  Cancel Movement
                </button>
                <p className="movement-hint">Click on a highlighted tile to move there</p>
              </>
            ) : attackMode ? (
              <>
                <button className="action-btn cancel-btn" onClick={cancelAttack}>
                  Cancel Attack
                </button>
                <p className="attack-hint">Click on a red-highlighted enemy to attack</p>
              </>
            ) : skillMode ? (
              <>
                <button className="action-btn cancel-btn" onClick={cancelSkill}>
                  Cancel Skill
                </button>
                <p className="skill-hint">
                  Using <strong>{selectedSkill?.skill.name}</strong> - Click on a target
                </p>
              </>
            ) : (
              <>
                <button
                  className="action-btn move-btn"
                  onClick={enterMovementMode}
                  disabled={
                    !selectedUnit ||
                    !selectedUnit.isPlayerUnit ||
                    selectedUnit.stats.currentMovement <= 0 ||
                    battle?.status === 'Victory' ||
                    battle?.status === 'Defeat' ||
                    !currentTurnUnit ||
                    currentTurnUnit.unitId !== selectedUnit.id ||
                    isUnitRooted(selectedUnit) ||
                    isUnitStunned(selectedUnit)
                  }
                  title={getDisabledReason(selectedUnit, 'move') || undefined}
                >
                  Move Unit ({selectedUnit?.stats.currentMovement || 0} pts)
                </button>
                <button
                  className="action-btn attack-btn"
                  onClick={enterAttackMode}
                  disabled={
                    !selectedUnit ||
                    !selectedUnit.isPlayerUnit ||
                    selectedUnit.stats.currentAP <= 0 ||
                    battle?.status === 'Victory' ||
                    battle?.status === 'Defeat' ||
                    !currentTurnUnit ||
                    currentTurnUnit.unitId !== selectedUnit.id ||
                    isUnitStunned(selectedUnit)
                  }
                  title={getDisabledReason(selectedUnit, 'attack') || undefined}
                >
                  Attack ({selectedUnit?.stats.currentAP || 0} AP)
                </button>
                <button
                  className="action-btn end-turn-btn"
                  onClick={endTurn}
                  disabled={!currentTurnUnit || !currentTurnUnit.isPlayerUnit || battle?.status === 'Victory' || battle?.status === 'Defeat'}
                >
                  End Turn
                </button>

              </>
            )}

            {actionError && (
              <div className="action-error">
                {actionError}
                <button onClick={() => setActionError(null)} className="close-error">×</button>
              </div>
            )}
          </div>

          {/* Debug & Controls Box */}
          <div className="debug-controls-box">
            <button
              className="action-btn"
              onClick={async () => {
                try {
                  const response = await makeAuthenticatedRequest(`/battles/${battleId}/instant-win`, {
                    method: 'POST'
                  });
                  if (response.ok) {
                    const result = await response.json();
                    setBattle(result.battleState);
                    const loot = result.materialsAwarded || [];
                    const experience = result.experience || 0;
                    const tilesUnlocked = result.tilesUnlocked || [];
                    const bonuses = result.bonuses || null;
                    setVictoryData({ experience, loot, tilesUnlocked, bonuses });
                    setShowVictoryScreen(true);
                    addEnhancedBattleLog('victory', {});
                  }
                } catch (err) {
                  console.error('Instant win failed:', err);
                }
              }}
              disabled={battle?.status === 'Victory' || battle?.status === 'Defeat'}
              style={{ backgroundColor: '#ff9800', width: '100%' }}
              title="DEBUG: Instantly win the battle (for testing)"
            >
              ⚡ Instant Win (DEBUG)
            </button>
            <button onClick={handleFleeBattle} className="exit-battle-btn" style={{ width: '100%' }}>
              Flee Battle
            </button>
            <button
              onClick={() => setShowLegend(!showLegend)}
              style={{ width: '100%', marginTop: '10px', backgroundColor: '#4CAF50' }}
              title="Show/hide tile color legend"
            >
              {showLegend ? '📖 Hide Legend' : '📖 Show Legend'}
            </button>
          </div>
        </div>

        {/* Tile Color Legend Panel */}
        {showLegend && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            border: '2px solid #4CAF50',
            borderRadius: '10px',
            padding: '20px',
            zIndex: 2000,
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>🗺️ Tile Color Legend</h3>
              <button onClick={() => setShowLegend(false)} style={{ background: '#f44336', border: 'none', color: 'white', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }}>
                ✖ Close
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              {/* Terrain Types */}
              <div>
                <h4 style={{ borderBottom: '2px solid #4CAF50', paddingBottom: '5px', marginBottom: '10px' }}>Terrain Types</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#90EE90', border: '1px solid #666' }}></div>
                    <span>Normal / Grass</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#228B22', border: '1px solid #666' }}></div>
                    <span>Forest (+Evasion)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#8B7355', border: '1px solid #666' }}></div>
                    <span>Mountain (+Defense)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#87CEEB', border: '1px solid #666' }}></div>
                    <span>Water (Slow)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#4682B4', border: '1px solid #666' }}></div>
                    <span>Deep Water (Impassable)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#F4A460', border: '1px solid #666' }}></div>
                    <span>Sand</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#556B2F', border: '1px solid #666' }}></div>
                    <span>Swamp (Slow, -Evasion)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#A9A9A9', border: '1px solid #666' }}></div>
                    <span>Road (Fast Movement)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#B0E0E6', border: '1px solid #666' }}></div>
                    <span>Ice (Slippery, -Evasion)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#FF6347', border: '1px solid #666' }}></div>
                    <span>Lava (Damage)</span>
                  </div>
                </div>
              </div>

              {/* Hazards & Special */}
              <div>
                <h4 style={{ borderBottom: '2px solid #FF9800', paddingBottom: '5px', marginBottom: '10px' }}>Hazards</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#FF4500', border: '1px solid #666' }}></div>
                    <span>Fire</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#9ACD32', border: '1px solid #666' }}></div>
                    <span>Poison</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#00CED1', border: '1px solid #666' }}></div>
                    <span>Ice</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#FFD700', border: '1px solid #666' }}></div>
                    <span>Lightning</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#ADFF2F', border: '1px solid #666' }}></div>
                    <span>Acid</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#8B4513', border: '1px solid #666' }}></div>
                    <span>Thorns</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '30px', height: '20px', backgroundColor: '#4B0082', border: '1px solid #666' }}></div>
                    <span>Miasma</span>
                  </div>
                </div>

                <h4 style={{ borderBottom: '2px solid #2196F3', paddingBottom: '5px', marginTop: '15px', marginBottom: '10px' }}>Overlays</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '14px' }}>
                  <div>🟦 <strong>Blue:</strong> Valid Move</div>
                  <div>🟥 <strong>Red:</strong> Valid Attack Target</div>
                  <div>🟪 <strong>Purple:</strong> Skill Target</div>
                  <div>🟨 <strong>Yellow:</strong> Skill Range</div>
                  <div>🟧 <strong>Orange:</strong> AOE Area</div>
                  <div>⚔️ <strong>Gray:</strong> Weapon Range</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BattleGrid;